const saysoIsLocalThemePreview = () =>
  ["127.0.0.1", "localhost", "::1"].includes(window.location.hostname);

const saysoLocalCartPreviewMessage =
  "Cart review is disabled on localhost to avoid Shopify verification. Use the Shopify preview link for cart testing.";

const saysoIsShopifyVerificationResponse = (response, responseText = "") =>
  response.status === 401 ||
  response.status === 403 ||
  /verifying your connection/i.test(responseText) ||
  /text\/html/i.test(response.headers.get("content-type") || "");

// Shopify routes from layout/theme.liquid (routes.*), so locale/market prefixes are kept.
const saysoRootUrl = () => {
  const root = window.saysoRoutes?.root || window.Shopify?.routes?.root || "/";
  return root.endsWith("/") ? root : `${root}/`;
};
const saysoRoute = (name) => window.saysoRoutes?.[name] || `${saysoRootUrl()}${{
  cart: "cart",
  cartAdd: "cart/add",
  cartChange: "cart/change",
  cartUpdate: "cart/update",
}[name]}`;

// One place for Shopify AJAX cart calls: JSON headers and body encoding.
// Requests return the raw Response so each caller keeps its own error handling;
// saysoCart.read() returns the parsed cart, or null when the request fails.
const saysoCartRequest = (path, body) =>
  fetch(
    path,
    body === undefined
      ? { headers: { Accept: "application/json" } }
      : {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
  );

const saysoCart = {
  get: () => saysoCartRequest(`${saysoRoute("cart")}.js`),
  add: (items) => saysoCartRequest(`${saysoRoute("cartAdd")}.js`, { items }),
  update: (updates) => saysoCartRequest(`${saysoRoute("cartUpdate")}.js`, { updates }),
  change: (id, quantity) => saysoCartRequest(`${saysoRoute("cartChange")}.js`, { id, quantity }),
  async read() {
    const response = await saysoCart.get();
    if (!response.ok) return null;
    return response.json();
  },
};

const saysoSubmitCartAddFallback = (items) => {
  const form = document.createElement("form");
  form.method = "post";
  form.action = saysoRoute("cartAdd");
  form.style.display = "none";

  const appendInput = (name, value) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.append(input);
  };

  if (items.length === 1) {
    appendInput("id", items[0].id);
    appendInput("quantity", String(items[0].quantity || 1));
  } else {
    items.forEach((item, index) => {
      appendInput(`items[${index}][id]`, item.id);
      appendInput(`items[${index}][quantity]`, String(item.quantity || 1));
    });
  }

  appendInput("return_to", saysoRoute("cart"));
  document.body.append(form);
  form.submit();
};

// Count chip flies from the add button into the header bag (assets/sayso-fly-to-bag.js).
// Resolves once it has landed; never throws, so a missing module can't block add to cart.
// While the chip is in the air the bag count waits for it (saysoUpdateHeaderCartCount), so the
// number ticks over as the "1" lands, not as it leaves the button.
let saysoFlightLanding = null;
// Beat between the "1" landing (bag bump + count) and the builder folding away.
const SAYSO_FOLD_AFTER_LAND_MS = 260;
const saysoAfterLandingBeat = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? Promise.resolve()
    : new Promise((resolve) => setTimeout(resolve, SAYSO_FOLD_AFTER_LAND_MS));
const saysoPlayAddFlight = (source, count = 1) => {
  const flight =
    typeof window.saysoFlyToBag === "function"
      ? Promise.resolve(window.saysoFlyToBag(source, count)).catch(() => {})
      : Promise.resolve();
  const landing = flight.then(() => {
    if (saysoFlightLanding === landing) saysoFlightLanding = null;
  });
  saysoFlightLanding = landing;
  return landing;
};

// Open the header bag drawer (assets/sayso-cart.js); falls back to the /cart page.
const saysoOpenCart = () => {
  if (window.saysoCartDrawer?.open?.()) return true;
  window.location.assign(saysoRoute("cart"));
  return false;
};

// wordwall: tell the page which saying is showing (snippets/wordwall.liquid swaps the background to match).
const saysoPublishSaying = (handle, title) => {
  if (!handle || (window.saysoSaying && handle === window.saysoSaying.handle)) return;
  window.saysoSaying = { handle, title };
  document.dispatchEvent(new CustomEvent("sayso:saying", { detail: window.saysoSaying }));
};
const saysoProductImageSizes = "(min-width: 760px) 560px, calc(100vw - 66px)";
const saysoImageCache = new Map();
const saysoDefaultDiscountTiers = [
  { minQuantity: 5, percent: 25 },
  { minQuantity: 4, percent: 20 },
  { minQuantity: 3, percent: 15 },
  { minQuantity: 2, percent: 10 },
];
// Pinned web-haptics@0.0.6 (+esm build) self-hosted in assets/. layout/theme.liquid
// passes its asset_url; the fallback resolves it next to this script.
const saysoHapticsModuleUrl =
  window.saysoHapticsModuleUrl ||
  new URL(
    "web-haptics-0.0.6.esm.js",
    document.currentScript?.src || window.location.href,
  ).href;
const saysoHapticFallbackPatterns = {
  selection: 8,
  light: 12,
  medium: 18,
  success: [35, 35, 35],
  error: [35, 35, 35, 35, 55],
};
// Interaction sounds (assets/sayso-sound.js: sayso.co's cues). No-op until it has loaded.
const saysoPlaySound = (cue, options) => window.saysoSound?.play(cue, options);
const saysoDigitRollStepMs = 74; // the digit roll's step (startSummaryDigitRoll)
// Price roll sound (one count·mech stretched to the roll, assets/sayso-sound.js): rows and footer
// share one cue. Falls ("back") when prices drop, rises ("forward") when they go up; nothing if
// the numbers didn't change. Compares the discount % first, then (footer, same %) the price.
// replace: this roll replaced one still running, so it gets its own cue.
const saysoPriceNumber = (text) => Number(String(text ?? "").replace(/[^0-9.]/g, "")) || 0;
const saysoPlayPriceSound = (duration, from, to, { replace = false } = {}) => {
  const delta =
    saysoPriceNumber(from.percent) - saysoPriceNumber(to.percent) ||
    saysoPriceNumber(to.price) - saysoPriceNumber(from.price);
  if (!delta) return;
  window.saysoSound?.price?.({ duration, direction: delta < 0 ? "back" : "forward", replace });
};
const saysoFooterPending = new WeakMap(); // root -> footer view shown before a change not yet slid
const saysoLastSubtotal = new WeakMap(); // root -> last non-empty footer view (not reactive)
const saysoReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
// Style flush, so a class taken off and put back in the same frame restarts its animation.
const saysoFlushStyles = (el) => void el?.offsetWidth;
// Is any match on screen (laid out, in the viewport) with the tab in front?
const saysoAnyOnScreen = (scope, selector) => {
  if (document.hidden || !scope) return false;
  return Array.from(scope.querySelectorAll(selector)).some((el) => {
    if (el.checkVisibility ? !el.checkVisibility({ visibilityProperty: true }) : !el.offsetParent) return false;
    const rect = el.getBoundingClientRect();
    return rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
  });
};
let saysoHaptics = null;
let saysoHapticsLoading = null;

const saysoHeaderBagLink = (scope = document) =>
  document.querySelector(".site-header .bag-link") ||
  scope?.querySelector?.(".site-header .bag-link") ||
  scope?.querySelector?.(".bag-link") ||
  null;

const saysoUpdateHeaderCartCount = (count, scope = document) => {
  if (saysoFlightLanding) {
    saysoFlightLanding.then(() => saysoUpdateHeaderCartCount(count, scope));
    return;
  }
  const bagLink = saysoHeaderBagLink(scope);
  if (!bagLink) return;

  const itemCount = Math.max(0, Number(count || 0));
  let countEl = bagLink.querySelector("[data-cart-count], .bag-count");

  if (itemCount <= 0) {
    countEl?.remove();
    return;
  }

  if (!countEl) {
    countEl = document.createElement("span");
    countEl.className = "bag-count";
    countEl.dataset.cartCount = "true";
    bagLink.append(countEl);
  }

  countEl.dataset.cartCount = "true";
  countEl.textContent = String(itemCount);
};

const saysoCurrentHeaderCartCount = (scope = document) => {
  const bagLink = saysoHeaderBagLink(scope);
  const countText =
    bagLink?.querySelector("[data-cart-count], .bag-count")?.textContent || "";
  const count = Number.parseInt(countText, 10);
  return Number.isFinite(count) ? count : 0;
};

const saysoInitAccordiStickyHeader = () => {
  const root = document.querySelector(".single-product--accordi");
  const header = root?.querySelector(".site-header");
  if (!root || !header || root.dataset.saysoStickyHeaderReady === "true") {
    return;
  }

  root.dataset.saysoStickyHeaderReady = "true";
  const focusable = Array.from(
    header.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
    ),
  );
  focusable.forEach((element) => {
    if (element.hasAttribute("tabindex")) {
      element.dataset.saysoOriginalTabindex = element.getAttribute("tabindex");
    }
  });

  const setHeaderVisible = (visible) => {
    root.classList.toggle("is-site-header-visible", visible);
    header.setAttribute("aria-hidden", visible ? "false" : "true");
    if ("inert" in header) {
      header.inert = !visible;
    }

    focusable.forEach((element) => {
      if (visible) {
        if (element.dataset.saysoOriginalTabindex !== undefined) {
          element.setAttribute("tabindex", element.dataset.saysoOriginalTabindex);
        } else {
          element.removeAttribute("tabindex");
        }
      } else {
        element.setAttribute("tabindex", "-1");
      }
    });
  };

  setHeaderVisible(true);
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", saysoInitAccordiStickyHeader, {
    once: true,
  });
} else {
  saysoInitAccordiStickyHeader();
}

const saysoLoadHaptics = () => {
  if (saysoHaptics) return Promise.resolve(saysoHaptics);
  if (saysoHapticsLoading) return saysoHapticsLoading;

  saysoHapticsLoading = import(saysoHapticsModuleUrl)
    .then(({ WebHaptics }) => {
      if (!WebHaptics) return null;
      saysoHaptics = new WebHaptics();
      return saysoHaptics;
    })
    .catch(() => null);

  return saysoHapticsLoading;
};

const saysoTriggerHaptic = (preset = "selection") => {
  if (saysoHaptics) {
    void saysoHaptics.trigger(preset);
    return;
  }

  const fallback = saysoHapticFallbackPatterns[preset];
  if (fallback && "vibrate" in window.navigator) {
    window.navigator.vibrate(fallback);
    void saysoLoadHaptics();
    return;
  }

  void saysoLoadHaptics().then((haptics) => {
    if (haptics) void haptics.trigger(preset);
  });
};

// Same moments as sayso.co (site-astro src/lib/haptics.ts): sayso-sound.js uses this for menu/bag
// open + close (light) and menu link presses (selection).
window.saysoHaptic = saysoTriggerHaptic;

const saysoDiscountTiersFromConfig = (tiers) => {
  const source =
    Array.isArray(tiers) && tiers.length ? tiers : saysoDefaultDiscountTiers;

  return source
    .map((tier) => {
      const minQuantity = Number(tier?.minQuantity);
      const percent =
        tier?.percent !== undefined
          ? Number(tier.percent)
          : Number(tier?.rate) * 100;

      if (
        !Number.isFinite(minQuantity) ||
        minQuantity < 1 ||
        !Number.isFinite(percent) ||
        percent <= 0
      ) {
        return null;
      }

      return {
        minQuantity,
        percent,
        rate: percent / 100,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.minQuantity - a.minQuantity);
};

const saysoColourImageKey = (colour) => {
  const value = String(colour || "").trim().toLowerCase();
  if (value.includes("pink")) return "pink";
  if (value.includes("blue")) return "blue";
  if (
    value.includes("white") ||
    value.includes("natural") ||
    value.includes("cream")
  )
    return "white";
  return "";
};

const saysoPlaceholderImageForColour = (placeholderImages, colour) => {
  const key = saysoColourImageKey(colour);
  return (key && placeholderImages?.[key]) || "";
};

// The product JSON carries one image URL (width=1100) per product/variant;
// rebuild the responsive srcset from it here instead of shipping four URLs each.
const saysoImageWidths = [420, 720, 960, 1100];
const saysoSrcsetFromImage = (src) => {
  if (!src || !/[?&]width=\d+/.test(src)) return "";
  return saysoImageWidths
    .map((width) => `${src.replace(/([?&]width=)\d+/, `$1${width}`)} ${width}w`)
    .join(", ");
};
const saysoExpandProductJson = (data) => {
  if (!data) return data;
  const expandVariants = (variants) =>
    (variants || []).forEach((variant) => {
      if (variant.image && !variant.imageSrcset) {
        variant.imageSrcset = saysoSrcsetFromImage(variant.image);
      }
    });
  (data.products || []).forEach((builderProduct) => {
    if (builderProduct.featuredImage && !builderProduct.featuredImageSrcset) {
      builderProduct.featuredImageSrcset = saysoSrcsetFromImage(
        builderProduct.featuredImage,
      );
    }
    expandVariants(builderProduct.variants);
  });
  expandVariants(data.variants);
  return data;
};

const saysoSelectionImageSource = ({
  variant,
  product,
  colour,
  placeholderImages,
}) => {
  if (variant?.image) {
    return {
      src: variant.image,
      srcset: variant.imageSrcset || "",
      sizes: saysoProductImageSizes,
    };
  }

  if (product?.image) {
    return {
      src: product.image,
      srcset: product.imageSrcset || "",
      sizes: saysoProductImageSizes,
    };
  }

  const placeholder = saysoPlaceholderImageForColour(placeholderImages, colour);
  if (placeholder) {
    return {
      src: placeholder,
      srcset: "",
      sizes: saysoProductImageSizes,
    };
  }

  return null;
};

const saysoImageSource = (source) => {
  if (!source) return null;
  if (typeof source === "string") return { src: source };
  if (source.src) return source;
  if (source.image) {
    return {
      src: source.image,
      srcset: source.imageSrcset || source.srcset || "",
      sizes: source.imageSizes || source.sizes || saysoProductImageSizes,
    };
  }
  return null;
};

const saysoPreloadImage = (source, priority = "low") => {
  const imageSource = saysoImageSource(source);
  if (!imageSource?.src) return Promise.resolve(null);

  const cacheKey = [
    imageSource.src,
    imageSource.srcset || "",
    imageSource.sizes || "",
  ].join("|");

  const cached = saysoImageCache.get(cacheKey);
  if (cached) {
    if ("fetchPriority" in cached.image && priority === "high") {
      cached.image.fetchPriority = "high";
    }
    return cached.promise;
  }

  const image = new Image();
  image.decoding = "async";
  if ("fetchPriority" in image) image.fetchPriority = priority;
  if (imageSource.srcset) image.srcset = imageSource.srcset;
  if (imageSource.sizes) image.sizes = imageSource.sizes;

  const entry = { image, promise: null, ready: false };
  entry.promise = new Promise((resolve) => {
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
  })
    .then((loadedImage) => {
      if (loadedImage?.decode) {
        return loadedImage.decode().then(
          () => loadedImage,
          () => loadedImage,
        );
      }
      return loadedImage;
    })
    .then((loadedImage) => {
      entry.ready = true;
      return loadedImage;
    });

  image.src = imageSource.src;
  saysoImageCache.set(cacheKey, entry);
  return entry.promise;
};
// Has this photo already loaded and decoded (so it can be shown this frame)?
const saysoImageReady = (source) => {
  const imageSource = saysoImageSource(source);
  if (!imageSource?.src) return true;
  const cacheKey = [imageSource.src, imageSource.srcset || "", imageSource.sizes || ""].join("|");
  return Boolean(saysoImageCache.get(cacheKey)?.ready);
};

const saysoScheduleIdle = (callback, timeout = 1200) => {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(callback, { timeout });
    return;
  }
  window.setTimeout(callback, Math.min(timeout, 350));
};

// Colour/print photo warming waits until the page load event (then idle time),
// so it never competes with the visible hoodie photo, fonts or scripts.
const saysoAfterLoad = (callback, timeout = 1200) => {
  if (document.readyState === "complete") {
    saysoScheduleIdle(callback, timeout);
    return;
  }
  window.addEventListener("load", () => saysoScheduleIdle(callback, timeout), {
    once: true,
  });
};

const saysoAutoSaveTimers = new Map();
window.saysoAlpineAccordiLab = () => {
  const root = document.querySelector("[data-single-product]");
  const productDataEl = root?.querySelector("[data-product-json]");
  const product = productDataEl
    ? saysoExpandProductJson(JSON.parse(productDataEl.textContent))
    : null;
  const placeholderImages = product?.placeholderImages || {};
  const discountTiers = saysoDiscountTiersFromConfig(product?.discountTiers);
  const savingsBurstUnmountDelayMs = 1960;
  // New builder: wait for its 520ms open, glide into view, then burst once
  // the page is still so the bubble isn't fighting the scroll/collapse.
  const newBuilderAnchorDelayMs = 560;
  const newBuilderAnchorDurationMs = 620;
  // Beat after the rows' price drop so the new prices can be read before the glide.
  const newBuilderAnchorPauseMs = 700;
  const builderProducts =
    Array.isArray(product?.products) && product.products.length
      ? product.products
      : [
	          {
	            id: product?.id || "",
	            title: product?.title || "Hoodie",
	            handle: product?.handle || "hoodie",
            featuredImage: product?.featuredImage || "",
            options: product?.options || [],
            variants: product?.variants || [],
          },
        ];
  const optionNames = builderProducts[0]?.options || product?.options || [];
  const colourIndex = optionNames.findIndex((option) => {
    const name = String(option || "").toLowerCase();
    return name.includes("colour") || name.includes("color");
  });
  const sizeIndex = optionNames.findIndex((option) =>
    String(option || "").toLowerCase().includes("size"),
  );
  const allVariants = builderProducts.flatMap(
    (builderProduct) => builderProduct.variants || [],
  );
  const canonical = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();
  const cleanPrintTitle = (value) => {
    if (canonical(value).includes("blank") || canonical(value).includes("no print")) {
      return "Blank";
    }

    return String(value || "")
      .replace(/["“”]/g, "")
      .replace(/^sayso\s+/i, "")
      .replace(/\s+hoodie$/i, "")
      .trim();
  };
  const optionValues = (index) => {
    if (index < 0) return [];
    return [
      ...new Set(allVariants.map((variant) => variant.options[index]).filter(Boolean)),
    ];
  };
  const preferredValue = (values, preferred) =>
    preferred
      .map((target) => values.find((value) => canonical(value) === target))
      .find(Boolean) || values[0] || "";

  const colours = optionValues(colourIndex);
  const sizes = optionValues(sizeIndex);
		  const prints = builderProducts.map((builderProduct) => ({
		    id: builderProduct.id || "",
		    title: builderProduct.title,
		    handle: builderProduct.handle,
	    image: builderProduct.featuredImage || "",
	    imageSrcset: builderProduct.featuredImageSrcset || "",
	    variants: builderProduct.variants || [],
	  }));
		  const cartLineVariantId = (line) =>
		    String(line?.variant_id || line?.id || "").trim();
	  const printIndexByVariantId = prints.reduce((map, print, printIndex) => {
	    (print.variants || []).forEach((variant) => {
	      const id = String(variant.id || "").trim();
	      if (id) map.set(id, printIndex);
	    });
	    return map;
	  }, new Map());
	  const slugify = (value) =>
	    canonical(value)
      .replace(/["“”']/g, "")
      .replace(/&/g, " and ")
      .replace(/hoodie/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  const urlSelectionParamNames = [
    "p",
    "saying",
    "print",
    "product",
    "handle",
    "c",
    "colour",
    "color",
    "s",
    "size",
  ];
  const urlSelectionParamSet = new Set(urlSelectionParamNames);
  const urlParams = () => new URLSearchParams(window.location.search);
  const cartModeParam = () => canonical(urlParams().get("cart_mode"));
  const shouldAppendCartDraftFromUrl = () => cartModeParam() === "add";
  const firstUrlParam = (...names) => {
    const params = urlParams();
    for (const name of names) {
      const value = params.get(name);
      if (value && value.trim()) return value;
    }
    return "";
  };
  const hasUrlSelectionPreset = () => {
    const params = urlParams();
    return urlSelectionParamNames.some((name) => params.has(name));
  };
	  const matchingOptionValue = (values, requestedValue) => {
	    if (!requestedValue) return "";
	    const requested = slugify(requestedValue);
    return (
      values.find(
        (value) =>
          value === requestedValue ||
          canonical(value) === canonical(requestedValue) ||
          slugify(value) === requested,
	      ) || ""
	    );
	  };
	  const lineOptionValue = (line, optionName, optionIndex) => {
	    const optionTokens = [
	      canonical(optionName),
	      slugify(optionName),
	      optionIndex === colourIndex ? "colour" : "",
	      optionIndex === colourIndex ? "color" : "",
	      optionIndex === sizeIndex ? "size" : "",
	    ].filter(Boolean);
	    const option = (line?.options_with_values || []).find((candidate) => {
	      const name = canonical(candidate?.name);
	      const slug = slugify(candidate?.name);
	      return optionTokens.some(
	        (token) => name === token || name.includes(token) || slug === token,
	      );
	    });

	    if (option?.value) return option.value;
	    return Array.isArray(line?.variant_options)
	      ? line.variant_options[optionIndex] || ""
	      : "";
	  };
	  const printIndexForUrlValue = (value) => {
	    if (!value) return -1;
    const requested = slugify(value);
    const isBlankRequest = ["blank", "no-print", "none", "no"].includes(
      requested,
    );

    return prints.findIndex((print) => {
      if (isBlankRequest) {
        return (
          canonical(print.title).includes("blank") ||
          canonical(print.handle).includes("blank")
        );
      }

      return (
        print.title === value ||
        print.handle === value ||
        slugify(print.title) === requested ||
        slugify(print.handle) === requested ||
        slugify(cleanPrintTitle(print.title)) === requested ||
        slugify(`"${print.title}"`) === requested
      );
    });
  };
	  const urlPresetSource = () => {
	    const source = {};
	    const colour = matchingOptionValue(
	      colours,
	      firstUrlParam("c", "colour", "color"),
    );
    const size = matchingOptionValue(sizes, firstUrlParam("s", "size"));
    const printIndex = printIndexForUrlValue(
      firstUrlParam("p", "saying", "print", "product", "handle"),
    );

    if (colour) source.colour = colour;
    if (size) source.size = size;
    if (printIndex >= 0) source.printIndex = printIndex;
	
		    return Object.keys(source).length ? source : null;
		  };
	  const initialProductPresetSource = () => {
	    const initialId = String(product?.initialProductId || "").trim();
	    const initialHandle = String(product?.initialProductHandle || "").trim();
	    const initialIndex = prints.findIndex((print) => {
	      if (initialId && String(print.id || "").trim() === initialId) return true;
	      if (initialHandle && print.handle === initialHandle) return true;
	      return false;
	    });

	    return initialIndex >= 0 ? { printIndex: initialIndex } : null;
	  };
	  const defaultSelectionSource = () =>
	    (hasUrlSelectionPreset() ? urlPresetSource() : null) ||
	    initialProductPresetSource();
		  const printIndexForCartLine = (line) => {
	    const variantPrintIndex = printIndexByVariantId.get(cartLineVariantId(line));
	    if (Number.isInteger(variantPrintIndex)) return variantPrintIndex;

	    const handle =
	      line?.handle ||
	      String(line?.url || "").match(/\/products\/([^/?#]+)/)?.[1] ||
	      "";
	    const title = line?.product_title || line?.title || "";
	    const requestedHandle = slugify(handle);
	    const requestedTitle = slugify(title);

	    return prints.findIndex(
	      (print) =>
	        slugify(print.handle) === requestedHandle ||
	        slugify(print.title) === requestedTitle ||
	        slugify(cleanPrintTitle(print.title)) === requestedTitle,
	    );
	  };

	  return {
 		    items: [],
	    cartContextLoaded: false,
	    cartContextLoading: false,
	    cartHadBuilderItems: false,
	    editingExistingCart: false,
	    queueDirty: false,
	    existingCartBuilderVariantIds: [],
	    cartItemCount: saysoCurrentHeaderCartCount(root),
		    openId: null,
    nextId: 1,
	    cartBusy: false,
	    addingItem: false,
	    builderAnchorFrame: null,
	    builderAnchorTimer: null,
	    builderAnchorSeq: 0,
	    builderAnchorHold: 0,
	    builderAnchorGlide: null,
		    cartSyncRegistered: false,
		    cartStatus: "",
	    recentlyCartedItemIds: [],
	    // After add to cart: the builder (with its "in your cart" row) is closing under its
	    // summary row, which stays on top and turns into a plain row (see foldAfterAdd).
	    foldedCartId: null,
	    foldClose: null,
	    foldCloseSeq: 0,
	    // Just added, still open with "in your cart": IN CART ↑ stays above it until its close.
	    cueHeldIds: [],
	    // An in-cart row the shopper opened: the footer is down until it closes ({ id, wasTucked }).
	    reopenTuck: null,
	    // The × remove at the end of each hoodie header row (2+ hoodies). Set false to hide it.
	    showRemoveX: true,
	    // In-cart hoodies are edited in place: a quiet save ~0.6s after the last pick (timers live outside
	    // Alpine state). savingFields: what that save changes. cartEditNote: "Updated …" (+ Undo) or an error.
	    savingItemId: null,
	    savingFields: [],
	    cartEditNote: null,
	    cartEditSeq: 0,
	    foldFlashId: null,
	    emptyOpenSeq: 0, // stops a pending openBuilderAfterEmpty
	    emptyOpenPending: false,
	    emptyHoldTimer: null,
		    summaryDiscountQuantity: 1,
		    // Add another hoodie: prices keep the old discount until the new builder's bubble
		    // has played (revealNewBuilder releases it), so the drop is seen, not pre-applied.
		    summaryDiscountHeld: false,
		    summaryPriceAnimating: false,
		    // Quiet price syncs (page load, cart drawer): rows take the new discount without a roll.
		    summaryQuiet: 0,
		    summaryPriceEffect: "slot",
	    // Sticky subtotal (cart truth). Tucked below the screen while another hoodie is built
	    // (Add another hoodie tap -> its add to cart); shows a held snapshot meanwhile, so the
	    // total visibly changes (the rows' "slot", 380ms) once it is back up.
	    subtotalTucked: false,
	    subtotalHeld: null,
	    subtotalReleaseTimer: null,
	    subtotalPriceSliding: false,
    imageViewMode: "full",
	    summaryPriceTimer: null,
	    summaryPriceSeq: 0,
	    summaryHoldToken: 0,
	    summaryHoldTimer: null,
	    summaryRollEndsAt: 0,
	    panelHold: 0,
	    panelHoldsFooter: false,
	    panelHoldListening: false,
    celebrationDelayTimer: null,
    summaryPriceRoll: {
      active: false,
      fromQuantity: 1,
      toQuantity: 1,
      step: 0,
      maxSteps: 0,
      timer: null,
    },
    celebration: {
      visible: false,
      itemId: null,
      kicker: "",
      label: "",
      pieces: [],
      token: 0,
      timer: null,
    },
	    printSwipe: {
	      active: false,
	      itemId: null,
	      pointerId: null,
	      source: null,
	      startX: 0,
	      startY: 0,
	      currentX: 0,
      currentY: 0,
      startedAt: 0,
      lock: null,
      frame: null,
    },
    colours,
	    sizes,
	    prints,
    init() {
	      const preset = defaultSelectionSource();
	      const first = this.createItem(preset);
      if (!this.sizeAvailable(first, first.size)) {
        first.size =
          this.sizes.find((size) => this.sizeAvailable(first, size)) ||
          first.size;
      }
      this.items = [first];
      this.openId = first.id;
      // wordwall: follow the open hoodie's print (cycle, swipe, open another card).
      Alpine.effect(() => {
        const open = this.items.find((item) => item.id === this.openId) || this.items[0];
        const print = open && this.printFor(open);
        if (print) saysoPublishSaying(print.handle, cleanPrintTitle(print.title));
      });
      this.summaryDiscountQuantity = this.discountQuantity();
      this.watchSummaryDiscount();
      this.watchCartSubtotalChanges();
      this.watchCartEmptied();
      // Let the rendered image keep high priority. Warm only nearby choices
      // later and at low priority so they cannot compete with first paint.
      saysoAfterLoad(() => {
        const current =
          this.items.find((item) => item.id === this.openId) || this.items[0];
        if (current) this.warmAdjacentImages(current, "low");
      }, 1600);
      if (preset && hasUrlSelectionPreset()) {
        this.syncUrlToItem(first);
      }
	      this.registerCartSync();
	      void this.syncCartCountForDiscounts();
	      if (this.shouldUseExistingCartContext()) {
	        void this.loadExistingCartContext();
	      }
	      this._onPrintKeydown = (event) => {
	        if (event.defaultPrevented) return;
	        if (event.metaKey || event.ctrlKey || event.altKey) return;
	        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
	        const target = event.target;
	        const tag = (target && target.tagName) || "";
	        if (
	          tag === "INPUT" ||
	          tag === "TEXTAREA" ||
	          tag === "SELECT" ||
	          (target && target.isContentEditable)
	        ) {
	          return;
	        }
	        const item =
	          this.items.find((candidate) => candidate.id === this.openId) ||
	          this.items[0];
	        if (!item || (this.prints || []).length <= 1) return;
	        event.preventDefault();
	        void this.cyclePrint(
	          item,
	          event.key === "ArrowLeft" ? "previous" : "next",
	        );
	      };
	      window.addEventListener("keydown", this._onPrintKeydown);
			    },
	    destroy() {
	      if (this._onPrintKeydown) {
	        window.removeEventListener("keydown", this._onPrintKeydown);
	        this._onPrintKeydown = null;
	      }
	    },
	    createItem(source = null) {
	      return {
	        id: this.nextId++,
	        colour:
	          source?.colour ||
	          preferredValue(this.colours, ["white", "natural", "cream"]),
	        size: source?.size || preferredValue(this.sizes, ["m", "medium"]),
		        printIndex: Number.isInteger(source?.printIndex) ? source.printIndex : 0,
		        cartBacked: Boolean(source?.cartBacked),
	        cartVariantId: String(source?.cartVariantId || ""),
		        imageLoading: true,
	        shown: null, // picks whose photo is still on screen while the new one loads (showPhotoWhenReady)
	        photoToken: 0,
	      };
	    },
		    ensureItemSizeAvailable(item) {
		      if (item && !this.sizeAvailable(item, item.size)) {
		        item.size =
		          this.sizes.find((size) => this.sizeAvailable(item, size)) ||
		          item.size;
		      }

		      return item;
		    },
	    markQueueDirty() {
	      this.queueDirty = true;
	    },
	    registerCartSync() {
	      if (this.cartSyncRegistered) return;
	      this.cartSyncRegistered = true;
	      window.addEventListener("pageshow", (event) => {
	        if (!event.persisted || this.queueDirty || this.cartBusy) return;
	        void this.loadExistingCartContext({ allowAppendDraft: false });
	      });
	      // Bag drawer edits (assets/sayso-cart.js): keep IN CART rows + discounts in step.
	      document.addEventListener("sayso:cart-changed", () => void this.syncWithCart());
	      // Bag changed in another tab: catch up when this one is looked at again.
	      document.addEventListener("visibilitychange", async () => {
	        if (document.hidden || this.cartBusy || this.cartContextLoading) return;
	        const cart = await saysoCart.read().catch(() => null);
	        if (!cart || document.hidden || this.cartBusy) return;
	        if (Math.max(0, Number(cart.item_count || 0)) !== Number(this.cartItemCount || 0)) void this.syncWithCart();
	      });
	    },
	    // Rebuild the IN CART rows from Shopify's cart; keep any hoodie still being built.
	    async syncWithCart() {
	      if (this.cartBusy || this.cartContextLoading) return;
	      // Edits in the bag drawer: the rows and footer are under it, so they keep the numbers
	      // they had and roll to the new ones when it closes (see holdUnderPanel).
	      if (document.querySelector("[popover][data-state='open']")) this.holdUnderPanel();
	      await this.syncWithCartNow();
	    },
	    async syncWithCartNow() {
	      this.holdPageForEmpty();
	      const drafts = this.items.filter((item) => !item?.cartBacked);
	      const openDraft = drafts.find((item) => item.id === this.openId) || null;
	      this.queueDirty = false;
	      await this.loadExistingCartContext({ allowAppendDraft: false });
	      const cartRows = this.cartHadBuilderItems
	        ? this.items.filter((item) => item?.cartBacked)
	        : [];
	      const nextDrafts = drafts.length || cartRows.length
	        ? drafts
	        : [this.ensureItemSizeAvailable(this.createItem(defaultSelectionSource()))];
	      // Bag emptied: the fresh builder opens on its own collapse (watchCartEmptied), not in place.
	      const emptied = !cartRows.length && (this.emptyOpenPending || this.items.some((item) => item?.cartBacked));
	      this.items = [...cartRows, ...nextDrafts];
	      const open = openDraft || (emptied ? null : nextDrafts[nextDrafts.length - 1]) || null;
	      this.openId = open ? open.id : null;
	      if (!emptied) this.releaseEmptyHold();
	      this.queueDirty = drafts.length > 0;
	      // Rows' discount: watchSummaryDiscount (rolls it, or keeps it while the drawer is over them).
	    },
	    createItemFromCartLine(line, printIndex) {
	      const cartVariantId = String(cartLineVariantId(line) || "");
			      const item = this.createItem({
	        printIndex,
	        cartBacked: true,
	        cartVariantId,
	        colour: matchingOptionValue(
	          this.colours,
	          lineOptionValue(line, optionNames[colourIndex], colourIndex),
	        ),
	        size: matchingOptionValue(
	          this.sizes,
	          lineOptionValue(line, optionNames[sizeIndex], sizeIndex),
	        ),
	      });

	      if (!item.colour) {
	        item.colour = preferredValue(this.colours, ["white", "natural", "cream"]);
	      }
	      if (!item.size) {
	        item.size = preferredValue(this.sizes, ["m", "medium"]);
	      }

		      this.ensureItemSizeAvailable(item);
	      item.cartFields = this.cartFieldsOf(item);
	      return item;
		    },
	    setCartModeUrl(mode = "") {
	      if (!window.history?.replaceState) return;

	      const params = urlParams();
	      params.delete("add_to_existing_cart");
	      if (mode) {
	        params.set("cart_mode", mode);
	      } else {
	        params.delete("cart_mode");
	      }

	      const query = params.toString();
	      const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
	      if (nextUrl !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
	        window.history.replaceState(window.history.state, "", nextUrl);
	      }
	    },
	    normaliseCartModeUrl(appendedDraft = false) {
	      if (appendedDraft) {
	        this.setCartModeUrl("edit");
	        return;
	      }

	      if (urlParams().has("add_to_existing_cart")) {
	        this.setCartModeUrl(cartModeParam() || "edit");
	      }
	    },
	    syncUrlToItem(item) {
      if (!item || !window.history?.replaceState) return;

      const print = this.printFor(item);
      const params = urlParams();

      Array.from(params.keys()).forEach((key) => {
        const keyPrefix = canonical(key).split(/\s+/)[0];
        if (urlSelectionParamSet.has(keyPrefix)) params.delete(key);
      });

      if (print?.handle) {
        params.set("p", print.handle);
      } else if (print?.title) {
        params.set("p", slugify(print.title));
      }

      if (item.colour) params.set("c", item.colour);
      if (item.size) params.set("s", item.size);

      const query = params.toString();
      const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
      if (nextUrl !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
        window.history.replaceState(window.history.state, "", nextUrl);
      }
    },
	    itemCardElement(id) {
      const entry = Array.from(
        root?.querySelectorAll("[data-alpine-item-id]") || [],
      ).find((candidate) => candidate.dataset.alpineItemId === String(id));

      return entry?.querySelector(".alpine-hoodie-card") || null;
	    },
	    builderAnchorOffset() {
      const header = root?.querySelector(".site-header");
      const headerHeight = header?.offsetHeight || 0;

      return headerHeight + 10;
	    },
	    builderAnchorTargetTop(id) {
      const card = this.itemCardElement(id);
      if (!card) return null;

      return Math.max(
        0,
        window.scrollY + card.getBoundingClientRect().top - this.builderAnchorOffset(),
      );
	    },
	    alignItemToBuilderAnchor(id) {
      const targetTop = this.builderAnchorTargetTop(id);
      if (targetTop === null) return false;

      window.scrollTo({ top: targetTop, behavior: "auto" });
      return true;
	    },
	    clearBuilderAnchorMotion({ keepHold = false } = {}) {
      if (this.builderAnchorTimer) {
        window.clearTimeout(this.builderAnchorTimer);
        this.builderAnchorTimer = null;
      }
      if (this.builderAnchorFrame) {
        window.cancelAnimationFrame(this.builderAnchorFrame);
        this.builderAnchorFrame = null;
      }
      this.builderAnchorSeq += 1; // stops a running followOpenToAnchor
      // A row opening in its place keeps the page-height hold: dropping it mid-glide would let
      // the page clamp the scroll (a jump back) before the new glide takes over.
      if (!keepHold) this.releaseBuilderAnchorHold();
	    },
	    // Page height held (min-height) so a row's glide can scroll past the page's current end;
	    // the page only reaches its full height as the row finishes opening.
	    holdBuilderAnchorScroll(scrollTop) {
      const need = Math.ceil(scrollTop + window.innerHeight);
      if (need <= document.scrollingElement.scrollHeight) return;
      this.builderAnchorHold = need;
      document.documentElement.style.minHeight = `${need}px`;
	    },
	    releaseBuilderAnchorHold() {
      if (!this.builderAnchorHold) return;
      this.builderAnchorHold = 0;
      document.documentElement.style.minHeight = "";
	    },
	    // A row opening (tapped, or Edit): its top glides up to the anchor under the header on the
	    // x-collapse's own curve, read off the panel's height each frame, so the move and the open
	    // start and stop on the same frames: one motion. It follows where the row is on screen, so
	    // a row above closing at the same time is absorbed instead of shoving it. The end is
	    // worked out once the open starts, for the page as it will be (opened, others closed) and
	    // clamped to where that page can scroll, so there is no snap at the end. Reduced motion:
	    // one cut to the end, held there while the panel opens. stay: the row keeps its place on
	    // screen (no anchor); the page only moves if it would end up shorter than the scroll.
	    followOpenToAnchor(id, { stay = false } = {}) {
      // A glide still moving (another row tapped mid-open): carry its speed into this one,
      // fading out as this row opens, so the page doesn't stop dead and start again.
      const last = this.builderAnchorGlide;
      const carried = last && performance.now() - last.at < 60 ? last.speed : 0;
      this.clearBuilderAnchorMotion({ keepHold: true });
      this.endFoldClose(); // its end would clear the page-height hold mid-glide
      const card = this.itemCardElement(id);
      if (!card) return;
      const seq = ++this.builderAnchorSeq;
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const partsOf = (scope) =>
        Array.from(scope.querySelectorAll(".alpine-hoodie-card > .alpine-hoodie-body, .alpine-hoodie-card > .alpine-hoodie-footer"))
          .filter((el) => window.getComputedStyle(el).display !== "none");
      const startTop = card.getBoundingClientRect().top;
      const followedAt = performance.now();
      let endTop = null;
      let startedAt = 0;
      const pin = (top) => {
        const delta = card.getBoundingClientRect().top - top;
        if (Math.abs(delta) < 0.5) return;
        const target = Math.max(0, window.scrollY + delta);
        this.holdBuilderAnchorScroll(target);
        const now = performance.now();
        const prev = this.builderAnchorGlide;
        const dt = prev ? now - prev.at : 0;
        this.builderAnchorGlide = {
          at: now,
          y: target,
          speed: prev && dt > 0 && dt < 60 ? (target - prev.y) / dt : 0,
        };
        window.scrollTo(0, target);
      };
      // Carried speed (scroll px/ms) as an on-screen offset: starts at that speed, gone by the
      // end of the open; never past where the glide ends.
      let coast = 0; // ms: how long the carried speed lasts, sized to the distance left
      const carry = (progress) => {
        if (!carried || reduceMotion) return 0;
        if (!coast) coast = endTop === null ? 80 : Math.min(80, (0.5 * Math.abs(endTop - startTop)) / Math.abs(carried));
        const t = performance.now() - followedAt;
        return -carried * coast * (1 - Math.exp(-t / coast)) * (1 - progress) ** 2;
      };
      const towards = (top) => {
        if (endTop === null) return top;
        return endTop < startTop ? Math.max(endTop, top) : Math.min(endTop, top);
      };
      const finish = () => {
        if (seq !== this.builderAnchorSeq) return;
        window.cancelAnimationFrame(this.builderAnchorFrame);
        window.clearTimeout(this.builderAnchorTimer);
        this.builderAnchorFrame = null;
        this.builderAnchorTimer = null;
        this.releaseBuilderAnchorHold();
        if (endTop !== null && this.openId === id && card.isConnected) pin(endTop);
      };
      const step = (now) => {
        if (seq !== this.builderAnchorSeq) return;
        if (this.openId !== id || !card.isConnected) return finish();
        const parts = partsOf(card);
        if (!parts.length) {
          pin(startTop + carry(0)); // not opening yet: hold still (or coast) while anything above moves
          this.builderAnchorFrame = window.requestAnimationFrame(step);
          return;
        }
        const height = parts.reduce((sum, el) => sum + el.offsetHeight, 0);
        const full = parts.reduce((sum, el) => sum + Math.max(el.scrollHeight, el.offsetHeight), 0);
        if (endTop === null) {
          startedAt = now;
          // The finished page: this row open, the rest (closing now) gone.
          const cardTop = card.getBoundingClientRect().top;
          let closing = 0;
          let closingAbove = 0;
          partsOf(root).filter((el) => !card.contains(el)).forEach((el) => {
            closing += el.offsetHeight;
            if (el.getBoundingClientRect().top < cardTop) closingAbove += el.offsetHeight;
          });
          const scroll = window.scrollY;
          const hold = this.builderAnchorHold;
          if (hold) document.documentElement.style.minHeight = "";
          const pageNow = document.scrollingElement.scrollHeight;
          if (hold) document.documentElement.style.minHeight = `${hold}px`;
          window.scrollTo(0, scroll);
          const page = pageNow + (full - height) - closing;
          const cardDocTop = scroll + cardTop - closingAbove;
          const maxScroll = Math.max(0, page - window.innerHeight);
          const want = stay ? cardDocTop - cardTop : cardDocTop - this.builderAnchorOffset();
          const endScroll = Math.max(0, Math.min(want, maxScroll));
          endTop = cardDocTop - endScroll;
        }
        const progress = full > 0 ? Math.min(1, height / full) : 1;
        pin(reduceMotion ? endTop : towards(startTop + (endTop - startTop) * progress + carry(progress)));
        if (progress >= 0.999 && now > startedAt) return finish();
        this.builderAnchorFrame = window.requestAnimationFrame(step);
      };
      this.builderAnchorFrame = window.requestAnimationFrame(step);
      this.builderAnchorTimer = window.setTimeout(finish, 1500); // safety net
	    },
	    animateItemToBuilderAnchor(id, duration = 720) {
      const targetTop = this.builderAnchorTargetTop(id);
      if (targetTop === null) return false;

      if (this.builderAnchorFrame) {
        window.cancelAnimationFrame(this.builderAnchorFrame);
      }

      const startTop = window.scrollY;
      const distance = targetTop - startTop;
      if (Math.abs(distance) < 2) return true;

      const startedAt = performance.now();
      const easeOutCubic = (progress) => 1 - Math.pow(1 - progress, 3);
      const step = (now) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        window.scrollTo(0, startTop + distance * easeOutCubic(progress));

        if (progress < 1) {
          this.builderAnchorFrame = window.requestAnimationFrame(step);
        } else {
          this.builderAnchorFrame = null;
        }
      };

      this.builderAnchorFrame = window.requestAnimationFrame(step);
      return true;
	    },
	    scheduleBuilderAnchor(id, options = {}) {
      const {
        delay = 0,
        duration = 720,
        mode = "auto",
        retries = [0, 80, 180, 360, 560],
      } = options;
      this.clearBuilderAnchorMotion();

      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          if (mode === "smooth") {
            this.builderAnchorTimer = window.setTimeout(() => {
              this.builderAnchorTimer = null;
              this.animateItemToBuilderAnchor(id, duration);
            }, delay);
            return;
          }

          retries.forEach((retryDelay) => {
            window.setTimeout(() => this.alignItemToBuilderAnchor(id), retryDelay);
          });
        });
      });
	    },
	    toggle(id) {
	      this.foldedCartId = null;
	      const shouldOpen = this.openId !== id;
	      // A glide still running for the row being left (its page-height hold stays if another opens).
	      this.clearBuilderAnchorMotion({ keepHold: shouldOpen });
	      const closing = this.items.find((item) => item.id === this.openId && item.cartBacked);
	      if (closing) this.closeCartRow(closing.id, { alone: !shouldOpen });
	      this.openId = shouldOpen ? id : null;
	      if (shouldOpen) {
	        this.followOpenToAnchor(id);
	        if (this.items.find((item) => item.id === id)?.cartBacked) this.holdSubtotalForRow(id);
	      }
	    },
	    // An in-cart row closing: its summary row stays on top and turns plain on the collapse's
	    // curve (is-fold-closing). Closing on its own: the same close as after an add, so the footer
	    // can ride its edge. With another row opening, that row's open moves the page instead.
	    closeCartRow(id, { alone = true } = {}) {
	      this.foldedCartId = id;
	      if (alone) this.startFoldClose(this.itemCardElement(id), id);
	      if (!this.foldClose) {
	        window.setTimeout(() => {
	          if (this.foldedCartId === id) this.foldedCartId = null;
	        }, 520); // x-collapse.duration.520ms
	      }
	    },
	    // Footer slides down (as for Add another) while an in-cart row the shopper opened is open,
	    // and back up when that row closes (see watchCartSubtotalChanges).
	    holdSubtotalForRow(id) {
	      const wasTucked = this.reopenTuck ? this.reopenTuck.wasTucked : this.subtotalTucked;
	      this.reopenTuck = { id, wasTucked };
	      this.tuckSubtotal();
	    },
	    releaseSubtotalForRow() {
	      const hold = this.reopenTuck;
	      if (!hold || this.openId === hold.id) return;
	      if (this.items.find((item) => item.id === this.openId)?.cartBacked) {
	        this.reopenTuck = { ...hold, id: this.openId }; // delete opened the next in-cart row: stays down
	        return;
	      }
	      this.reopenTuck = null;
	      if (hold.wasTucked) return; // it was already down (a hoodie being built): stays down
	      this.untuckSubtotal({ withFold: this.foldClose?.id === hold.id });
	    },
	    inCartCue(item) {
	      return Boolean(item?.cartBacked) && !this.cueHeldIds.includes(item.id);
	    },
	    cartCueAfter(index) {
	      return this.inCartCue(this.items[index]) && !this.inCartCue(this.items[index + 1]);
	    },
	    setImageViewMode(mode) {
	      this.imageViewMode = mode === "detail" ? "detail" : "full";
	      saysoTriggerHaptic("selection");
	    },
    toggleImageViewMode() {
      this.setImageViewMode(this.imageViewMode === "detail" ? "full" : "detail");
    },
	    imageViewModeLabel() {
	      return this.imageViewMode === "detail" ? "Detail" : "Full";
	    },
	    resetPrintSwipe(element = null) {
      if (this.printSwipe.frame) {
        window.cancelAnimationFrame(this.printSwipe.frame);
      }

      const pointerId = this.printSwipe.pointerId;
      if (
        element &&
        pointerId !== null &&
        element.hasPointerCapture?.(pointerId)
      ) {
        element.releasePointerCapture(pointerId);
      }

      if (element) {
        element.classList.remove("is-swiping");
        element.style.setProperty("--alpine-swipe-x", "0px");
      }

	      this.printSwipe = {
	        active: false,
	        itemId: null,
	        pointerId: null,
	        source: null,
	        startX: 0,
	        startY: 0,
	        currentX: 0,
        currentY: 0,
        startedAt: 0,
        lock: null,
        frame: null,
      };
    },
    startPrintSwipeAt(event, item, source, pointerId, clientX, clientY) {
	      if (this.prints.length <= 1) return;
      if (event.target?.closest?.("button, a, input, select, textarea")) return;

	      this.resetPrintSwipe(event.currentTarget);
	      this.printSwipe = {
	        active: true,
	        itemId: item.id,
        pointerId,
        source,
	        startX: clientX,
	        startY: clientY,
	        currentX: clientX,
	        currentY: clientY,
	        startedAt: window.performance?.now?.() || Date.now(),
	        lock: null,
	        frame: null,
	      };
	      event.currentTarget.classList.add("is-swiping");
	    },
	    startPrintSwipe(event, item) {
	      if (event.button !== undefined && event.button !== 0) return;
      this.startPrintSwipeAt(
        event,
        item,
        "pointer",
        event.pointerId,
        event.clientX,
        event.clientY,
      );
	    },
    queuePrintSwipeOffset(element, offset) {
      if (!this.printSwipe.active) return;
      if (this.printSwipe.frame) return;

      this.printSwipe.frame = window.requestAnimationFrame(() => {
        this.printSwipe.frame = null;
        element.style.setProperty("--alpine-swipe-x", `${offset}px`);
      });
    },
    movePrintSwipeAt(event, item, source, pointerId, clientX, clientY) {
	      if (
	        !this.printSwipe.active ||
	        this.printSwipe.itemId !== item.id ||
        this.printSwipe.source !== source ||
	        this.printSwipe.pointerId !== pointerId
	      ) {
	        return;
	      }

	      this.printSwipe.currentX = clientX;
	      this.printSwipe.currentY = clientY;

	      const deltaX = clientX - this.printSwipe.startX;
	      const deltaY = clientY - this.printSwipe.startY;
	      const absX = Math.abs(deltaX);
	      const absY = Math.abs(deltaY);

      if (!this.printSwipe.lock) {
        if (absX < 8 && absY < 8) return;

        if (absY > absX * 1.08) {
          this.resetPrintSwipe(event.currentTarget);
          return;
        }

	        if (absX > absY * 1.18) {
	          this.printSwipe.lock = "horizontal";
          if (source === "pointer") {
            event.currentTarget.setPointerCapture?.(pointerId);
          }
	        }
      }

      if (this.printSwipe.lock !== "horizontal") return;

      event.preventDefault();
      const maxOffset = Math.min(
        96,
        Math.max(58, event.currentTarget.clientWidth * 0.14),
      );
	      const offset = Math.max(-maxOffset, Math.min(maxOffset, deltaX * 0.32));
	      this.queuePrintSwipeOffset(event.currentTarget, offset);
	    },
	    movePrintSwipe(event, item) {
      this.movePrintSwipeAt(
        event,
        item,
        "pointer",
        event.pointerId,
        event.clientX,
        event.clientY,
      );
    },
    async finishPrintSwipeAt(event, item, source, pointerId, clientX, clientY) {
	      if (
	        !this.printSwipe.active ||
	        this.printSwipe.itemId !== item.id ||
        this.printSwipe.source !== source ||
	        this.printSwipe.pointerId !== pointerId
	      ) {
	        return;
	      }

      if (Number.isFinite(clientX) && Number.isFinite(clientY)) {
        this.printSwipe.currentX = clientX;
        this.printSwipe.currentY = clientY;
      }

	      const element = event.currentTarget;
	      const deltaX = this.printSwipe.currentX - this.printSwipe.startX;
      const deltaY = Math.abs(this.printSwipe.currentY - this.printSwipe.startY);
      const elapsed =
        (window.performance?.now?.() || Date.now()) - this.printSwipe.startedAt;
      const threshold = Math.min(92, Math.max(44, element.clientWidth * 0.12));
      const velocity = Math.abs(deltaX) / Math.max(1, elapsed);
      const shouldChange =
        this.printSwipe.lock === "horizontal" &&
        Math.abs(deltaX) > deltaY &&
        (Math.abs(deltaX) >= threshold ||
          (Math.abs(deltaX) >= 32 && velocity >= 0.45));

      if (!shouldChange) {
        this.resetPrintSwipe(element);
        return;
      }

      event.preventDefault();
      const direction = deltaX > 0 ? "previous" : "next";
      const exitOffset = deltaX > 0 ? threshold : -threshold;
      this.queuePrintSwipeOffset(element, exitOffset);

      try {
        await this.cyclePrint(item, direction);
      } finally {
	        window.setTimeout(() => this.resetPrintSwipe(element), 80);
	      }
	    },
	    async finishPrintSwipe(event, item) {
	      await this.finishPrintSwipeAt(
	        event,
	        item,
	        "pointer",
	        event.pointerId,
	        event.clientX,
	        event.clientY,
	      );
	    },
		    cancelPrintSwipe(event) {
      if (event?.type === "lostpointercapture" && this.printSwipe.active) {
        return;
      }

		      this.resetPrintSwipe(event.currentTarget);
		    },
	    startPrintTouch(event, item) {
      if (window.PointerEvent) return;
	      if (event.touches.length !== 1) return;
	      const touch = event.changedTouches[0];
	      this.startPrintSwipeAt(
	        event,
	        item,
        "touch",
        touch.identifier,
        touch.clientX,
        touch.clientY,
	      );
	    },
	    movePrintTouch(event, item) {
      if (window.PointerEvent) return;
	      const pointerId = this.printSwipe.pointerId;
	      const touch = Array.from(event.touches).find(
	        (candidate) => candidate.identifier === pointerId,
	      );
	      if (!touch) return;
      this.movePrintSwipeAt(
        event,
        item,
        "touch",
        pointerId,
        touch.clientX,
        touch.clientY,
	      );
	    },
	    async finishPrintTouch(event, item) {
      if (window.PointerEvent) return;
	      const pointerId = this.printSwipe.pointerId;
	      const touch = Array.from(event.changedTouches).find(
	        (candidate) => candidate.identifier === pointerId,
	      );
      await this.finishPrintSwipeAt(
        event,
        item,
        "touch",
        pointerId,
        touch?.clientX,
        touch?.clientY,
      );
    },
		    async addItem() {
	      this.flushAutoSaves();
	      if (this.addingItem) return;
	      this.foldedCartId = null;

	      this.addingItem = true;
	      this.markQueueDirty();
	      if (this.celebrationDelayTimer) {
        window.clearTimeout(this.celebrationDelayTimer);
        this.celebrationDelayTimer = null;
      }
      saysoTriggerHaptic("success");
      const source =
        this.items.find((item) => item.id === this.openId) || this.items[0];
	      const next = this.createItem(source);
	      next.colour = preferredValue(this.colours, ["white", "natural", "cream"]);
	      next.cartBacked = false;

      if (this.openId !== null) {
        this.openId = null;
        await new Promise((resolve) => setTimeout(resolve, 260));
      }

      const hold = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? 0
        : this.holdSummaryDiscount();
      this.items.push(next);
      await this.$nextTick();

      requestAnimationFrame(() => {
        this.openId = next.id;
        this.addingItem = false;
        void this.revealNewBuilder(next.id, hold);
      });
    },
    // New builder is open. In order: the new builder's savings bubble plays, then the rows
    // above re-discount, then (only once both have finished) the page glides down to the
    // new builder. Reduced motion: no animations, snap straight there.
    async revealNewBuilder(itemId, hold = 0) {
      const openedAt = performance.now();
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduceMotion) {
        this.releaseSummaryDiscount(hold, { animate: false });
        if (this.openId !== itemId) return;
        this.scheduleBuilderAnchor(itemId);
        this.scheduleSavingsBurst(itemId, newBuilderAnchorDelayMs + 60);
        return;
      }
      // Bubble waits for the 520ms open so it isn't fighting the expand.
      // Moved on before the bubble is done (added it to the cart, opened a row): stop waiting, so
      // the rows don't sit on the old discount for the rest of the bubble.
      await new Promise((resolve) => {
        const movedOn = window.setInterval(() => {
          if (this.openId === itemId) return;
          window.clearInterval(movedOn);
          resolve();
        }, 100);
        const done = () => { window.clearInterval(movedOn); resolve(); };
        this.celebrationDelayTimer = window.setTimeout(() => {
          this.celebrationDelayTimer = null;
          if (!this.items.some((item) => item.id === itemId)) return done();
          const shown = this.triggerSavingsBurst(this.discountQuantity(), itemId, { syncRows: false });
          window.setTimeout(done, shown ? savingsBurstUnmountDelayMs : 0);
        }, newBuilderAnchorDelayMs);
      });
      if (this.openId !== itemId) {
        // Moved on meanwhile (added it to the cart, opened a row): the rows still drop here, with
        // their roll, but the page isn't yanked.
        this.releaseSummaryDiscount(hold);
        return;
      }
      if (hold !== this.summaryHoldToken) return; // a newer Add another owns the prices now
      const rowsDrop = this.releaseSummaryDiscount(hold);
      await rowsDrop;
      if (this.openId !== itemId) return;
      const delay = Math.max(
        newBuilderAnchorPauseMs,
        newBuilderAnchorDelayMs - (performance.now() - openedAt),
      );
      this.scheduleBuilderAnchor(itemId, {
        delay,
        duration: newBuilderAnchorDurationMs,
        mode: "smooth",
      });
    },
    scheduleSavingsBurst(itemId, settleMs = newBuilderAnchorDelayMs + newBuilderAnchorDurationMs + 60) {
      // Fire after the new builder has opened and the anchor scroll has landed
      // (or straight after the open when no scroll is needed).
      const fire = () => {
        this.celebrationDelayTimer = null;
        if (this.items.some((item) => item.id === itemId)) {
          this.triggerSavingsBurst(this.discountQuantity(), itemId);
        }
      };
      this.celebrationDelayTimer = window.setTimeout(fire, settleMs);
    },
    triggerSavingsBurst(quantity, itemId = null, { syncRows = true } = {}) {
      const rate = this.discountRateForQuantity(quantity);
      if (!rate) return false;

      if (this.celebration.timer) {
        window.clearTimeout(this.celebration.timer);
      }
	      if (this.summaryPriceTimer) {
	        window.clearTimeout(this.summaryPriceTimer);
	      }

	      const token = this.celebration.token + 1;
      const percent = Math.round(rate * 100);
      const palette = ["#11100f", "#fffdfa", "#f4bfd2", "#acd7ee", "#f1ebe2"];
      const pieces = Array.from({ length: 42 }, (_, index) => {
        const angle = -165 + (330 / 41) * index + ((index % 3) - 1) * 7;
        const distance = 68 + (index % 7) * 13 + (index % 2) * 18;
        const x = Math.cos((angle * Math.PI) / 180) * distance;
        const y =
          Math.sin((angle * Math.PI) / 180) * distance -
          22 -
          (index % 5) * 8;
        const isDash = index % 4 === 0;
        const isDot = index % 5 === 0;

        return {
          id: `${token}-${index}`,
          x: Math.round(x),
          y: Math.round(y),
          rotate: Math.round(angle * 1.6 + index * 19),
          delay: (index % 6) * 18,
          width: isDash ? 16 : isDot ? 7 : 9,
          height: isDash ? 4 : isDot ? 7 : 9,
          radius: isDot ? 999 : index % 3 === 0 ? 0 : 2,
          colour: palette[index % palette.length],
        };
      });

      this.celebration = {
        visible: true,
        itemId,
        kicker: `${quantity} ${quantity === 1 ? "Hoodie" : "Hoodies"} =`,
        label: `${percent}% OFF`,
        pieces,
        token,
        timer: window.setTimeout(() => {
          if (this.celebration.token === token) {
            this.celebration.visible = false;
          }
        }, savingsBurstUnmountDelayMs),
      };

	      if (syncRows) this.summaryPriceTimer = window.setTimeout(() => {
	        if (this.celebration.token === token) {
	          this.setSummaryDiscountQuantity(this.discountQuantity(), true);
	        }
	      }, 850);
      return true;
    },
    // Resolves when the summary price animation has finished (straight away if none runs).
    setSummaryDiscountQuantity(quantity, animate = false) {
      if (this.summaryDiscountQuantity === quantity) return Promise.resolve();

      const previousQuantity = this.summaryDiscountQuantity;
	      const nextEffect = animate
	        ? this.summaryPriceEffectForQuantity(quantity, previousQuantity)
	        : this.summaryPriceEffect;

      this.stopSummaryDigitRoll();
      this.summaryDiscountQuantity = quantity;

      if (!animate) return Promise.resolve();

      this.summaryPriceEffect = nextEffect;
      if (this.summaryPriceEffect === "count") {
        this.startSummaryDigitRoll(previousQuantity, quantity);
      }
      const roll = ++this.summaryPriceSeq;
      const restart = this.summaryPriceAnimating;
      this.summaryPriceAnimating = false;
      // New numbers and the roll class land in the same frame (a rAF here left one frame of the
      // new price unrolled when called from a frame callback, e.g. the fold). A roll already
      // running restarts: its class comes off and back on across a style flush.
      return new Promise((resolve) => this.$nextTick(() => {
        if (roll !== this.summaryPriceSeq) return resolve();
        if (restart) saysoFlushStyles(this.$root);
        this.summaryPriceAnimating = true;
        const animationDuration =
          this.summaryPriceEffect === "count"
            ? Math.min(980, 240 + this.summaryPriceRoll.maxSteps * 72)
            : {
	                unfold: 680,
	                slot: 380,
	              }[this.summaryPriceEffect] || 420;
        // Sound only while someone can see it roll: a summary row on screen, tab in front. Rows
        // that scroll into view mid-roll (the glide to a new builder) sound for what is left;
        // one cue per roll, so rows scrolling out and back in don't play it twice.
        const rollEnd = performance.now() + animationDuration;
        this.summaryRollEndsAt = rollEnd;
        let sounded = false;
        let seenBefore = false;
        const soundWhenSeen = () => {
          if (roll !== this.summaryPriceSeq) return;
          const left = rollEnd - performance.now();
          if (left < 20) return;
          const seen = saysoAnyOnScreen(this.$root, ".alpine-hoodie-header .sticky-summary-price-cluster");
          if (seen && !sounded && left >= 240) {
            sounded = true;
            saysoPlayPriceSound(
              left,
              { percent: this.summaryPercentNumberForQuantity(previousQuantity) },
              { percent: this.summaryPercentNumberForQuantity(quantity) },
              // A roll this replaced had its own cue: this one gets a fresh one (footer's slot aside).
              { replace: restart && !this.subtotalPriceSliding },
            );
          } else if (!seen && seenBefore && sounded && !this.subtotalPriceSliding) {
            // Rows scrolled out of view mid-roll (the glide to a new builder): a cue still
            // waiting for the "1" landing is dropped.
            window.saysoSound?.priceCut?.(0);
          }
          seenBefore = seen;
          requestAnimationFrame(soundWhenSeen);
        };
        soundWhenSeen();
        window.setTimeout(() => {
          // A newer roll owns the class and digits; this one's end must not cut it short.
          if (roll === this.summaryPriceSeq) {
            this.summaryPriceAnimating = false;
            if (this.summaryPriceEffect === "count") {
              this.stopSummaryDigitRoll();
            }
          }
          resolve();
        }, animationDuration);
      }));
	    },
	    // Add another hoodie: the rows keep their old discount until the new builder's bubble has
	    // played. Each hold has its own token, so only the Add another that took it can let it go
	    // (an older one finishing late, or its safety timer, can't release a newer hold early).
	    holdSummaryDiscount({ safetyMs = 8000 } = {}) {
	      window.clearTimeout(this.summaryHoldTimer);
	      const token = ++this.summaryHoldToken;
	      this.summaryDiscountHeld = true;
	      if (safetyMs) this.summaryHoldTimer = window.setTimeout(() => this.releaseSummaryDiscount(token), safetyMs); // never stuck
	      return token;
	    },
	    // A cart change while a site panel (the bag drawer) covers the page: rows and footer keep
	    // what they show until it closes, then roll to the new numbers where they can be seen.
	    holdUnderPanel() {
	      if (saysoReducedMotion()) return;
	      if (!this.summaryDiscountHeld) this.panelHold = this.holdSummaryDiscount({ safetyMs: 0 });
	      if (!this.subtotalHeld && !this.subtotalTucked) {
	        const view = this.cartSubtotalView();
	        if (view.quantity) { this.subtotalHeld = view; this.panelHoldsFooter = true; }
	      }
	      if (this.panelHoldListening) return;
	      this.panelHoldListening = true;
	      document.addEventListener("site-panel:close", () => {
	        this.panelHoldListening = false;
	        window.setTimeout(() => {
	          if (this.panelHold) this.releaseSummaryDiscount(this.panelHold);
	          this.panelHold = 0;
	          if (this.panelHoldsFooter && !this.subtotalTucked) this.subtotalHeld = null;
	          this.panelHoldsFooter = false;
	        }, 160); // the drawer is mostly out of the way
	      }, { capture: true, once: true });
	    },
	    // Let the rows take the current discount, with their roll (the drop the hold was keeping
	    // for). Resolves when it has played. token 0: whatever hold there is.
	    releaseSummaryDiscount(token = 0, { animate = true } = {}) {
	      if (token && token !== this.summaryHoldToken) return Promise.resolve();
	      window.clearTimeout(this.summaryHoldTimer);
	      this.summaryHoldTimer = null;
	      const drop = this.setSummaryDiscountQuantity(this.discountQuantity(), animate && !saysoReducedMotion());
	      this.summaryDiscountHeld = false;
	      return drop;
	    },
	    // The rows' shown discount (summaryDiscountQuantity) follows the hoodie count. Any change
	    // not made by an explicit call (a × remove, a cart add, an Add another that moved on, an
	    // error putting things back) rolls here, once, after the change's own task has finished
	    // (so a remove sees the cart count it also lowers). Quiet syncs and reduced motion snap.
	    watchSummaryDiscount() {
	      let queued = false;
	      const settle = () => {
	        queued = false;
	        if (this.summaryDiscountHeld || this.cartContextLoading) return;
	        const quantity = this.discountQuantity();
	        if (quantity === this.summaryDiscountQuantity) return;
	        const animate = !this.summaryQuiet && this.cartContextLoaded && !saysoReducedMotion();
	        void this.setSummaryDiscountQuantity(quantity, animate);
	      };
	      Alpine.effect(() => {
	        this.discountQuantity();
	        void this.summaryDiscountHeld;
	        void this.cartContextLoading;
	        if (queued) return;
	        queued = true;
	        queueMicrotask(settle);
	      });
	    },
	    summaryPriceEffectForQuantity(quantity, previousQuantity = this.summaryDiscountQuantity) {
	      if (previousQuantity === 1 && quantity === 2) return "unfold";
	      return quantity >= 2 ? "count" : "slot";
	    },
    startSummaryDigitRoll(fromQuantity, toQuantity) {
      const maxSteps = this.summaryDigitRollMaxSteps(fromQuantity, toQuantity);
      this.summaryPriceRoll = {
        active: true,
        fromQuantity,
        toQuantity,
        direction: toQuantity >= fromQuantity ? "down" : "up",
        step: 0,
        maxSteps,
        timer: null,
      };

      const tick = () => {
        if (!this.summaryPriceRoll.active) return;
        this.summaryPriceRoll.step = Math.min(
          this.summaryPriceRoll.step + 1,
          this.summaryPriceRoll.maxSteps,
        );
        if (this.summaryPriceRoll.step < this.summaryPriceRoll.maxSteps) {
          this.summaryPriceRoll.timer = window.setTimeout(tick, saysoDigitRollStepMs);
        }
      };

      this.summaryPriceRoll.timer = window.setTimeout(tick, saysoDigitRollStepMs);
    },
    stopSummaryDigitRoll() {
      if (this.summaryPriceRoll?.timer) {
        window.clearTimeout(this.summaryPriceRoll.timer);
      }
      this.summaryPriceRoll = {
        active: false,
        fromQuantity: this.summaryDiscountQuantity,
        toQuantity: this.summaryDiscountQuantity,
        direction: "down",
        step: 0,
        maxSteps: 0,
        timer: null,
      };
    },
    summaryPriceIsCounting() {
      return (
        this.summaryPriceAnimating &&
        this.summaryPriceEffect === "count" &&
        this.summaryPriceRoll.active
      );
    },
    summaryDigitRollMaxSteps(fromQuantity, toQuantity) {
      const fromPercent = this.summaryPercentNumberForQuantity(fromQuantity);
      const toPercent = this.summaryPercentNumberForQuantity(toQuantity);
      const priceSteps = this.items.map((item) => {
        const fromPrice = this.summaryWholePriceNumber(
          this.summaryDiscountedPriceCentsForQuantity(item, fromQuantity),
        );
        const toPrice = this.summaryWholePriceNumber(
          this.summaryDiscountedPriceCentsForQuantity(item, toQuantity),
        );
        return this.summaryNumberDistance(fromPrice, toPrice);
      });

      return Math.max(
        1,
        this.summaryNumberDistance(fromPercent, toPercent),
        ...priceSteps,
      );
    },
    summaryNumberDistance(fromValue, toValue) {
      return Math.abs(Number(toValue) - Number(fromValue));
    },
    summaryRollingPercentParts() {
      const fromPercent = String(
        this.summaryPercentNumberForQuantity(this.summaryPriceRoll.fromQuantity),
      );
      const toPercent = String(
        this.summaryPercentNumberForQuantity(this.summaryPriceRoll.toQuantity),
      );
      return this.summaryRollingNumberParts("percent", fromPercent, toPercent);
    },
    summaryRollingPriceParts(item) {
      const fromPrice = this.summaryWholePriceNumber(
        this.summaryDiscountedPriceCentsForQuantity(
          item,
          this.summaryPriceRoll.fromQuantity,
        ),
      );
      const toPrice = this.summaryWholePriceNumber(
        this.summaryDiscountedPriceCentsForQuantity(
          item,
          this.summaryPriceRoll.toQuantity,
        ),
      );
      return this.summaryRollingNumberParts(
        `price-${item.id}`,
        fromPrice,
        toPrice,
      );
    },
    summaryRollingNumberParts(prefix, fromText, toText) {
      const fromNumber = Number(fromText);
      const toNumber = Number(toText);
      const direction = toNumber > fromNumber ? 1 : -1;
      const distance = Math.abs(toNumber - fromNumber);
      const currentNumber =
        distance === 0
          ? toNumber
          : fromNumber +
            direction * Math.min(this.summaryPriceRoll.step, distance);
      const currentText = String(currentNumber);
      const targetText = String(toNumber);
      const length = Math.max(String(fromNumber).length, targetText.length);
      const from = String(fromNumber).padStart(length, " ");
      const current = currentText.padStart(length, " ");
      const to = targetText.padStart(length, " ");
      const step = this.summaryPriceRoll.step;

      return Array.from({ length }, (_, index) => {
        const fromChar = from[index];
        const currentChar = current[index];
        const toChar = to[index];
        const changing = distance > 0 && /\d/.test(currentChar);
        const text = changing && step === 0 ? fromChar : currentChar;

        return {
          key: `${prefix}-${index}`,
          text: text === " " ? "" : text,
          changing,
          direction: this.summaryPriceRoll.direction,
        };
      });
    },
    confettiStyle(piece) {
      return {
        "--confetti-x": `${piece.x}px`,
        "--confetti-y": `${piece.y}px`,
        "--confetti-rotate": `${piece.rotate}deg`,
        "--confetti-delay": `${piece.delay}ms`,
        "--confetti-width": `${piece.width}px`,
        "--confetti-height": `${piece.height}px`,
        "--confetti-radius": `${piece.radius}px`,
        "--confetti-colour": piece.colour,
      };
    },
		    removeItem(id) {
		      if (this.items.length <= 1) return;
      const item = this.items.find((candidate) => candidate.id === id);
      const removedCartBackedItem = Boolean(item?.cartBacked);
      if (removedCartBackedItem && this.cartBusy) return;

      const previousState = {
        cartItemCount: this.cartItemCount,
        cartHadBuilderItems: this.cartHadBuilderItems,
        editingExistingCart: this.editingExistingCart,
        items: this.items.slice(),
        openId: this.openId,
        queueDirty: this.queueDirty,
        summaryDiscountQuantity: this.summaryDiscountQuantity,
      };
	      this.markQueueDirty();
	      const previousQuantity = this.summaryDiscountQuantity;
      const index = this.items.findIndex((candidate) => candidate.id === id);
      this.items = this.items.filter((candidate) => candidate.id !== id);
      if (this.openId === id) {
        const fallback = this.items[Math.min(index, this.items.length - 1)];
        this.openId = fallback?.id || null;
      }
	      if (this.summaryPriceTimer) {
	        window.clearTimeout(this.summaryPriceTimer);
	      }
      if (this.celebrationDelayTimer) {
        window.clearTimeout(this.celebrationDelayTimer);
        this.celebrationDelayTimer = null;
      }
      if (this.celebration.timer) {
        window.clearTimeout(this.celebration.timer);
      }
      this.celebration.visible = false;
	      this.summaryDiscountQuantity = previousQuantity;
	      // Its reveal can't finish now (its timer is cleared): let the hold go. The rows roll once,
	      // from watchSummaryDiscount, after the cart count below has dropped too.
	      if (this.summaryDiscountHeld) {
	        window.clearTimeout(this.summaryHoldTimer);
	        this.summaryHoldToken += 1; // its reveal, if it ever wakes, has nothing to let go
	        this.summaryDiscountHeld = false;
	      }
      if (removedCartBackedItem) {
        const nextCartItemCount = Math.max(
          0,
          Number(previousState.cartItemCount || saysoCurrentHeaderCartCount(root)) -
            1,
        );
        this.cartItemCount = nextCartItemCount;
        this.updateHeaderCartCount(nextCartItemCount);
        void this.syncCartBackedItemsAfterInlineRemove(previousState);
      }
		    },
	    imageLoading(item) {
	      return Boolean(item?.imageLoading && this.hoodieImage(item));
	    },
	    finishImageLoad(item, image) {
	      if (!item) return;
	      item.imageLoading = false;
	      if (image) this.retireSsrPhoto(image);
	    },
	    // The server-rendered first photo stays on top until a real card's photo has decoded, so
	    // the hand-over never shows a blank or shimmer frame.
	    retireSsrPhoto(image) {
	      const ssr = document.querySelector(".alpine-ssr-entry");
	      if (!ssr) return;
	      const done = () => {
	        image.setAttribute("data-photo-ready", "");
	        requestAnimationFrame(() => ssr.remove());
	      };
	      if (image.decode) image.decode().then(done, done);
	      else done();
	    },
	    imageSourceFor(item) {
	      const variant = this.variantFor(item);
	      const product = this.productFor(item);
	      return saysoSelectionImageSource({
	        variant,
	        product,
	        colour: item.colour,
	        placeholderImages,
	      });
	    },
	    warmImageFor(item, priority = "low") {
	      return saysoPreloadImage(this.imageSourceFor(item), priority);
	    },
	    warmAdjacentImages(item, priority = "high") {
	      const candidates = [item];
	      const colourPosition = this.colours.indexOf(item.colour);

	      if (colourPosition >= 0 && this.colours.length > 1) {
	        candidates.push({
	          ...item,
	          colour:
	            this.colours[
	              (colourPosition - 1 + this.colours.length) % this.colours.length
	            ],
	        });
	        candidates.push({
	          ...item,
	          colour: this.colours[(colourPosition + 1) % this.colours.length],
	        });
	      }

	      if (this.prints.length > 1) {
	        candidates.push({
	          ...item,
	          printIndex:
	            (item.printIndex - 1 + this.prints.length) % this.prints.length,
	        });
	        candidates.push({
	          ...item,
	          printIndex: (item.printIndex + 1) % this.prints.length,
	        });
	      }

	      return Promise.all(
	        candidates.map((candidate) => this.warmImageFor(candidate, priority)),
	      );
	    },
	    // The picks (swatch, size, print name, price, aria, in-cart "Updating…") change on the tap.
	    // The photo on screen stays until the new one has loaded and decoded, then swaps in one
	    // frame: no blank, no shimmer. Rapid taps each count; only the last photo is shown.
	    // Resolves once the photo for these picks is showing (or a newer pick took over).
	    selectionOf(item) {
	      return { colour: item.colour, printIndex: item.printIndex, size: item.size };
	    },
	    shownItem(item) {
	      return item?.shown ? { ...item, ...item.shown } : item;
	    },
	    showPhotoWhenReady(item, shownBefore) {
	      const token = (item.photoToken || 0) + 1;
	      item.photoToken = token;
	      const source = this.photoSourceFor(item);
	      if (saysoImageReady(source)) {
	        item.shown = null;
	        return Promise.resolve();
	      }
	      if (!item.shown) item.shown = shownBefore;
	      return saysoPreloadImage(source, "high").then(() => {
	        if (item.photoToken === token) item.shown = null;
	      });
	    },
	    pickSize(item) {
	      if (!this.sizeAvailable(item, item.size)) {
	        item.size =
	          this.sizes.find((size) => this.sizeAvailable(item, size)) ||
	          item.size;
	      }
	    },
	    afterPick(item, shownBefore) {
	      this.markQueueDirty();
	      this.syncUrlToItem(item);
	      const ready = this.showPhotoWhenReady(item, shownBefore);
	      this.warmAdjacentImages(item, "high");
	      return ready;
	    },
	    cyclePrint(item, direction) {
	      const length = this.prints.length || 1;
	      if (length <= 1) return Promise.resolve();
	      saysoTriggerHaptic("selection");
	      // Prev/next: mech close back, mech open forward.
	      saysoPlaySound(direction === "previous" ? "close" : "open", { theme: "mech" });
	      const shownBefore = this.selectionOf(this.shownItem(item));
	      const offset = direction === "previous" ? -1 : 1;
	      item.printIndex = (item.printIndex + offset + length) % length;
	      this.pickSize(item);
	      return this.afterPick(item, shownBefore);
	    },
	    setColour(item, colour) {
	      saysoTriggerHaptic("selection");
	      saysoPlaySound("select", { theme: "press" });
	      if (item.colour === colour) return Promise.resolve();
	      const shownBefore = this.selectionOf(this.shownItem(item));
	      item.colour = colour;
	      this.pickSize(item);
	      return this.afterPick(item, shownBefore);
	    },
	    setSize(item, size) {
	      if (!this.sizeAvailable(item, size)) return Promise.resolve();
	      saysoTriggerHaptic("selection");
	      saysoPlaySound("select", { theme: "press" });
	      if (item.size === size) return Promise.resolve();
	      const shownBefore = this.selectionOf(this.shownItem(item));
	      item.size = size;
	      return this.afterPick(item, shownBefore);
	    },
    sizeAvailable(item, size) {
      return this.productFor(item).variants.some(
        (variant) =>
          variant.available &&
          variant.options[colourIndex] === item.colour &&
          variant.options[sizeIndex] === size,
      );
    },
    productFor(item) {
      return this.prints[item.printIndex] || this.prints[0] || { variants: [] };
    },
    variantFor(item) {
      const variants = this.productFor(item).variants || [];
      return (
        variants.find(
          (variant) =>
            variant.available &&
            variant.options[colourIndex] === item.colour &&
            variant.options[sizeIndex] === item.size,
        ) ||
        variants.find(
          (variant) =>
            variant.options[colourIndex] === item.colour &&
            variant.options[sizeIndex] === item.size,
        ) ||
        variants[0]
      );
    },
	    printFor(item) {
	      return this.prints[item.printIndex] || this.prints[0];
	    },
    itemPriceCents(item) {
      return this.variantFor(item)?.priceCents || 0;
    },
	    discountRateForQuantity(quantity) {
	      return (
	        discountTiers.find((tier) => quantity >= tier.minQuantity)?.rate || 0
	      );
	    },
		    shouldUseExistingCartContext() {
		      // Always read the cart: refreshes and return visits must show hoodies
		      // already in the bag (IN CART rows), not just ?cart_mode links.
		      return Boolean(window.fetch);
		    },
		    shouldAppendCartDraft() {
		      // Plain visit (no cart_mode) or ?cart_mode=add: cart rows + a fresh
		      // open builder. ?cart_mode=edit (refresh after ATC): cart rows only.
		      return shouldAppendCartDraftFromUrl() || !cartModeParam();
		    },
			    async loadExistingCartContext({ allowAppendDraft = true } = {}) {
	      if (this.queueDirty || this.cartBusy) return;
	      const firstLoad = !this.cartContextLoaded; // page load: rows start on the cart's discount

	      this.cartContextLoading = true;
			      try {
			        const response = await saysoCart.get();
			        if (!response.ok) {
	          this.cartContextLoaded = true;
	          return;
	        }

		        const cart = await response.json();
	        if (this.queueDirty || this.cartBusy) return;

	        this.cartItemCount = Math.max(0, Number(cart?.item_count || 0));
	        this.updateHeaderCartCount(this.cartItemCount);

			        const cartItems = Array.isArray(cart?.items) ? cart.items : [];
			        const hydratedItems = [];
	        const builderVariantIds = new Set();

		        cartItems.forEach((line) => {
		          const printIndex = printIndexForCartLine(line);
		          const quantity = Math.max(0, Number(line?.quantity || 0));

		          if (printIndex < 0 || quantity <= 0) {
		            return;
		          }

	          const variantId = cartLineVariantId(line);
	          if (variantId) builderVariantIds.add(variantId);

		          for (let index = 0; index < quantity; index += 1) {
		            hydratedItems.push(this.createItemFromCartLine(line, printIndex));
		          }
		        });

		        if (hydratedItems.length) {
	          const shouldAppendDraft = allowAppendDraft && this.shouldAppendCartDraft();
	          const nextItems = [...hydratedItems];
	          let openItem = hydratedItems[hydratedItems.length - 1];

	          if (shouldAppendDraft) {
	            const newItem = this.ensureItemSizeAvailable(
		              this.createItem(defaultSelectionSource()),
	            );
	            nextItems.push(newItem);
	            openItem = newItem;
	          }

		          this.items = nextItems;
		          this.openId = openItem?.id || null;
	          this.cartHadBuilderItems = true;
		          this.editingExistingCart = true;
			          this.queueDirty = shouldAppendDraft;
		          this.existingCartBuilderVariantIds = Array.from(builderVariantIds);
		          this.normaliseCartModeUrl(shouldAppendDraft);
			          nextItems.slice(0, 3).forEach((item) => {
		            this.warmAdjacentImages(item, "high");
		          });
		        } else {
			          this.cartHadBuilderItems = false;
				          this.editingExistingCart = false;
	          this.existingCartBuilderVariantIds = [];
	          this.normaliseCartModeUrl(false);
			        }

	        this.cartContextLoaded = true;
		        // Later loads (drawer edits, back/forward) go through watchSummaryDiscount's roll.
		        if (firstLoad && !this.summaryDiscountHeld) this.summaryDiscountQuantity = this.discountQuantity();
			      } catch (error) {
		        this.cartContextLoaded = true;
			        this.cartHadBuilderItems = false;
				        this.editingExistingCart = false;
	        this.existingCartBuilderVariantIds = [];
	      } finally {
	        this.cartContextLoading = false;
			      }
			    },
	    discountQuantity(items = this.items) {
	      const list = Array.isArray(items) ? items : [];
	      const draftQuantity = list.filter((item) => !item?.cartBacked).length;
	      // Include hoodies already in the Shopify cart even when they are not
	      // mirrored as cart-backed builder rows (plain PDP with cart badge > 0).
	      const fromCart = Number(this.cartItemCount || 0) + draftQuantity;
	      return Math.max(list.length, fromCart);
	    },
	    reviewQuantity() {
	      return this.discountQuantity();
	    },
    cartActionDraftItem() {
      return (
        this.items.find((item) => !item.cartBacked && item.id === this.openId) ||
        this.items.find((item) => !item.cartBacked) ||
        this.items.find((item) => item.id === this.openId) ||
        this.items[0]
      );
    },
    cartActionDiscountQuantity(draftQuantity) {
      const resultingCartQuantity = Number(this.cartItemCount || 0) + draftQuantity;
      return Math.max(this.discountQuantity(), resultingCartQuantity);
    },
    cartActionItemPriceLabel(draftQuantity) {
      const item = this.cartActionDraftItem();
      const quantity = this.cartActionDiscountQuantity(draftQuantity);
      return this.wholePrice(this.summaryDiscountedPriceCentsForQuantity(item, quantity));
    },
		    cartButtonLabel(view = "auto") {
	      if (this.cartContextLoading) return "Checking cart";

      const cartBackedQuantity = this.items.filter((item) => item.cartBacked).length;
      const draftQuantity = this.items.filter((item) => !item.cartBacked).length;
		      const quantity = this.reviewQuantity();
	      const noun = quantity === 1 ? "hoodie" : "hoodies";
      const total = this.totalPriceLabel();

      if (!this.cartItemCount && !cartBackedQuantity && draftQuantity === 1) {
        return "Add to Cart";
      }

	      if (this.editingExistingCart) {
        if (!this.queueDirty || draftQuantity <= 0) return "View cart";

        if (draftQuantity === 1) {
          return `Add this hoodie to cart (${this.cartActionItemPriceLabel(draftQuantity)})`;
        }

        const draftTotal = this.wholePrice(
          this.discountedSubtotalCents(this.items.filter((item) => !item.cartBacked)),
        );
	        return `Add ${draftQuantity} hoodies to cart (${draftTotal})`;
	      }

      if (this.cartItemCount && draftQuantity === 1) {
        return `Add this hoodie to cart (${this.cartActionItemPriceLabel(draftQuantity)})`;
      }

	      return `Add ${quantity} ${noun} to cart (${total})`;
	    },
    isLocalThemePreview() {
      return saysoIsLocalThemePreview();
    },
    // The rows show summaryDiscountQuantity: it follows discountQuantity() (the same basis as
    // cartButtonLabel → cartActionItemPriceLabel) through watchSummaryDiscount, so every change
    // the shopper sees goes through setSummaryDiscountQuantity and its roll. (It used to take
    // the max with the live count, which showed a new discount unrolled whenever the hold let go
    // without a roll, then snapped back on the next Add another.)
    displayDiscountQuantity() {
      return Number(this.summaryDiscountQuantity || 0);
    },
    summaryDiscountRate() {
      return this.discountRateForQuantity(this.displayDiscountQuantity());
    },
    summaryDiscountLabel() {
      return `${Math.round(this.summaryDiscountRate() * 100)}% OFF`;
    },
    summaryPercentNumberForQuantity(quantity) {
      return Math.round(this.discountRateForQuantity(quantity) * 100);
    },
    summaryDiscountedPriceCentsForQuantity(item, quantity) {
      return Math.round(
        this.itemPriceCents(item) * (1 - this.discountRateForQuantity(quantity)),
      );
    },
    summaryDiscountedPriceCents(item) {
      return this.summaryDiscountedPriceCentsForQuantity(
        item,
        this.displayDiscountQuantity(),
      );
    },
    subtotalCents(items) {
      return items.reduce((sum, item) => sum + this.itemPriceCents(item), 0);
    },
	    discountedSubtotalCents(items = this.items) {
	      const rate = this.discountRateForQuantity(this.discountQuantity(items));
	      return items.reduce(
	        (sum, item) => sum + Math.round(this.itemPriceCents(item) * (1 - rate)),
	        0,
      );
    },
    ordinalNumber(number) {
      const value = Math.max(0, Number(number || 0));
      const teenRemainder = value % 100;
      const suffix =
        teenRemainder >= 11 && teenRemainder <= 13
          ? "th"
          : value % 10 === 1
            ? "st"
            : value % 10 === 2
              ? "nd"
              : value % 10 === 3
                ? "rd"
                : "th";

      return `${value}${suffix}`;
    },
    canShowAddAnotherHoodie() {
      const cartBackedCount = this.items.filter((item) => item.cartBacked).length;
      const draftCount = this.items.filter((item) => !item.cartBacked).length;
      // AAH shows after every add to cart: any cart-backed hoodie, no open draft.
      return cartBackedCount >= 1 && draftCount === 0;
    },
    addAnotherButtonLabel() {
      const nextQuantity = this.discountQuantity() + 1;
      const nextRate = this.discountRateForQuantity(nextQuantity);
      const scopeWord = nextQuantity === 2 ? "both" : `all ${nextQuantity}`;
      const discountText = nextRate
        ? ` and get ${Math.round(nextRate * 100)}% off ${scopeWord}`
        : "";
      const actionText =
        nextQuantity === 2
          ? `Add a ${this.ordinalNumber(nextQuantity)} hoodie`
          : "Add another hoodie";

      return `${actionText}${discountText}`;
    },
    async addAnotherHoodie(flyFrom = null) {
      if (this.cartBusy || this.cartContextLoading) return;
      this.reopenTuck = null; // Add another keeps the footer down now, for the new builder
      this.tuckSubtotal();
      const draftQuantity = this.items.filter((item) => !item.cartBacked).length;

      if (this.editingExistingCart && !this.queueDirty && draftQuantity === 0) {
        await this.addItem();
        return;
      }

      await this.addToCart({ continueBuilding: true, flyFrom });
    },
    wholePrice(cents) {
      return `$${Math.round((cents || 0) / 100)}`;
    },
    totalPriceLabel() {
      return this.wholePrice(this.discountedSubtotalCents(this.items));
    },
    devQueuedCartTotalLabel() {
      const quantity = this.reviewQuantity();
      return `REVIEW ${quantity} ${quantity === 1 ? "HOODIE" : "HOODIES"} — ${this.totalPriceLabel()}`;
    },
    summaryWholePriceNumber(cents) {
      return String(Math.round((cents || 0) / 100));
    },
	    printTitle(item) {
	      return cleanPrintTitle(this.printFor(item)?.title);
	    },
	    printLabel(item) {
	      const title = this.printTitle(item);
	      const originalPrice = this.wholePrice(this.itemPriceCents(item));
	      const discountedPrice = this.wholePrice(this.summaryDiscountedPriceCents(item));
	      return this.summaryDiscountRate() > 0
	        ? `${title} — ${originalPrice} ${discountedPrice}`
	        : `${title} — ${originalPrice}`;
	    },
    summaryTitle(item) {
      return `${cleanPrintTitle(this.printFor(item)?.title)} Hoodie`;
    },
    colourTokens(colour) {
      const value = canonical(colour);
      if (value.includes("pink")) return { background: "#e7cedb", border: "#c9a9ba" };
      if (value.includes("blue")) return { background: "#a9c7e2", border: "#85a9c9" };
      if (value.includes("black")) return { background: "#1b1b1b", border: "#1b1b1b" };
      if (value.includes("grey") || value.includes("gray")) return { background: "#b8b8b8", border: "#9e9e9e" };
      return { background: "#fffdfa", border: "#d7d3ce" };
    },
    colourStyle(colour) {
      const tokens = this.colourTokens(colour);
      return {
        "--summary-colour-bg": tokens.background,
        "--summary-colour-border": tokens.border,
      };
    },
	    // The exact src/srcset/sizes the card's <img> uses for these picks.
	    photoSourceFor(item) {
	      if (this.shouldUseLocalZoomAssets(item)) {
	        return { src: this.localZoomHoodieImage(item), srcset: "", sizes: saysoProductImageSizes };
	      }
	      return this.imageSourceFor(item);
	    },
	    hoodieImage(item) {
	      return this.photoSourceFor(this.shownItem(item))?.src || "";
	    },
	    hoodieImageSrcset(item) {
	      return this.photoSourceFor(this.shownItem(item))?.srcset || "";
	    },
	    hoodieImageSizes() {
	      return saysoProductImageSizes;
	    },
	    localZoomAssetBaseUrl() {
	      const assetUrl =
	        document.querySelector('script[src*="/assets/script.js"]')?.src ||
	        document.querySelector('link[href*="/assets/style.css"]')?.href ||
	        "";
	      const cleanUrl = assetUrl.split("?")[0];

	      return cleanUrl ? cleanUrl.replace(/\/assets\/[^/]+$/, "/assets/i") : "";
	    },
	    localZoomOverrideSlug() {
	      const requested = slugify(firstUrlParam("local_saying", "local_svg"));
	      return ["bias", "in-vain", "limits", "spaghetti"].includes(requested)
	        ? requested
	        : "";
	    },
	    localZoomPrintSlug(item) {
	      const override = this.localZoomOverrideSlug();
	      if (override) return override;

	      const print = this.printFor(item);
	      const slug = slugify(cleanPrintTitle(print?.title || print?.handle));

	      if (slug.includes("spaghetti")) return "spaghetti";
	      if (slug.includes("in-vain") || slug.includes("vain")) return "in-vain";
	      if (slug.includes("limit")) return "limits";
	      if (slug.includes("bias")) return "bias";

	      return "";
	    },
	    localZoomColourSlug(item) {
	      const colour = canonical(item?.colour);

	      if (colour.includes("pink")) return "pink";
	      if (colour.includes("blue")) return "blue";
	      if (colour.includes("white")) return "white";

	      return "";
	    },
	    shouldUseLocalZoomAssets(item) {
	      return Boolean(
	        saysoIsLocalThemePreview() &&
	          this.localZoomPrintSlug(item) &&
	          this.localZoomColourSlug(item),
	      );
	    },
	    localZoomHoodieImage(item) {
	      const colour = this.localZoomColourSlug(item);
	      return colour
	        ? `${this.localZoomAssetBaseUrl()}/sayso-hoodie-${colour}.webp`
	        : "";
	    },
	    printImage(item) {
	      item = this.shownItem(item);
	      if (!this.shouldUseLocalZoomAssets(item)) return "";

	      return `${this.localZoomAssetBaseUrl()}/sayso-${this.localZoomPrintSlug(item)}.svg`;
	    },
	    printImageStyle(item) {
	      if (!this.shouldUseLocalZoomAssets(item)) return {};

	      return {
	        "--print-overlay-height": "10.8%",
	        "--print-overlay-top": "46%",
	        "--print-overlay-left": "50%",
	      };
	    },
    cartPermalink(items, destination = "cart") {
      const quantitiesById = items.reduce((lines, item) => {
        const id = String(item.id || "").trim();
        if (!id) return lines;
        lines[id] = (lines[id] || 0) + Number(item.quantity || 1);
        return lines;
      }, {});
      const cartLines = Object.entries(quantitiesById)
        .map(([id, quantity]) => `${encodeURIComponent(id)}:${quantity}`)
        .join(",");

      const suffix = destination === "checkout" ? "?checkout" : "";
      return cartLines
        ? `${saysoRoute("cart")}/${cartLines}${suffix}`
        : destination === "cart"
          ? saysoRoute("cart")
          : `${saysoRootUrl()}${destination}`;
    },
    async refreshCartCount() {
      const cart = await saysoCart.read();
      if (!cart) return null;

      this.cartItemCount = Math.max(0, Number(cart.item_count || 0));
      this.updateHeaderCartCount(this.cartItemCount);
      return cart;
    },
    async syncCartCountForDiscounts() {
      // Plain PDP: no cart_mode hydrate, but bag may already have hoodies.
      // Pull live cart count so What saying? / header match the ATC price.
      if (this.queueDirty || this.cartBusy) return;
      this.summaryQuiet += 1; // page load: no roll, no sound
      try {
        await this.syncCartCountForDiscountsNow();
      } finally {
        this.summaryQuiet -= 1;
      }
    },
    async syncCartCountForDiscountsNow() {
      const fromHeader = saysoCurrentHeaderCartCount(root);
      if (fromHeader > Number(this.cartItemCount || 0)) {
        this.cartItemCount = fromHeader;
      }
      try {
        await this.refreshCartCount();
      } catch (error) {
        /* keep header-derived count */
      }
      this.setSummaryDiscountQuantity(this.discountQuantity(), false);
    },
	    updateHeaderCartCount(count) {
	      saysoUpdateHeaderCartCount(count, root);
	    },
    currentVariantId(item) {
      return String(this.variantFor(item)?.id || "");
    },
    markItemCartSynced(item) {
      if (!item) return;
      item.cartVariantId = this.currentVariantId(item);
      item.cartFields = { colour: item.colour, size: item.size, printIndex: item.printIndex };
      item.cartBacked = true;
    },
    cartBackedItemChanged(item) {
      if (!item?.cartBacked) return false;
      const currentId = this.currentVariantId(item);
      return Boolean(currentId && String(item.cartVariantId || "") !== currentId);
    },
    cartFieldsOf(item) {
      return { colour: item.colour, size: item.size, printIndex: item.printIndex };
    },
    // The picks that differ from what's in the cart, as status words ("colour", "size", "saying").
    changedCartFields(item) {
      const saved = item?.cartFields;
      if (!item?.cartBacked || !saved) return [];
      return [
        ["colour", "colour"],
        ["size", "size"],
        ["printIndex", "saying"],
      ]
        .filter(([key]) => saved[key] !== item[key])
        .map(([, word]) => word);
    },
    cartFieldsCopy(fields) {
      const words = fields.length ? fields : ["hoodie"];
      return words.length < 2 ? words[0] : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
    },
    // Called from each card's x-effect whenever its picks change: one save ~0.6s after the last pick,
    // so a run of print swipes or arrow taps saves only the print it stops on.
    scheduleAutoSave(item) {
      if (!item?.cartBacked || !this.cartBackedItemChanged(item)) return;
      if (this.cartEditNote?.id === item.id) this.setCartEditNote(null);
      window.clearTimeout(saysoAutoSaveTimers.get(item.id));
      saysoAutoSaveTimers.set(item.id, window.setTimeout(() => this.runAutoSave(item.id), 600));
    },
    runAutoSave(id) {
      window.clearTimeout(saysoAutoSaveTimers.get(id));
      saysoAutoSaveTimers.delete(id);
      const item = this.items.find((candidate) => candidate.id === id);
      if (!item || !this.cartBackedItemChanged(item)) return;
      if (this.cartBusy || this.cartContextLoading) {
        saysoAutoSaveTimers.set(id, window.setTimeout(() => this.runAutoSave(id), 300));
        return;
      }
      this.updateCartBackedItem(item, { quiet: true });
    },
    // Save anything pending now (View Cart, the bag icon, add another hoodie).
    flushAutoSaves() {
      Array.from(saysoAutoSaveTimers.keys()).forEach((id) => this.runAutoSave(id));
    },
    autoSavePending(item) {
      return Boolean(item && (this.cartBackedItemChanged(item) || this.savingItemId === item.id));
    },
    // The in-cart status line under the picks (also read out by the form's aria-live status).
    cartBackedStatusCopy(item) {
      if (this.autoSavePending(item)) {
        const fields = this.changedCartFields(item);
        return `Updating ${this.cartFieldsCopy(fields.length ? fields : this.savingFields)}…`;
      }
      const note = this.cartEditNote;
      if (note?.id === item?.id) return note.copy;
      return "This hoodie is in your cart.";
    },
    // "Updated …" (with Undo for ~5s) or an error, on one hoodie's status line.
    setCartEditNote(note, ms = 5000) {
      const seq = ++this.cartEditSeq;
      this.cartEditNote = note;
      if (note) window.setTimeout(() => { if (this.cartEditSeq === seq) this.cartEditNote = null; }, ms);
    },
    cartEditUndoable(item) {
      const note = this.cartEditNote;
      return Boolean(note?.undo && note.id === item?.id && !this.autoSavePending(item));
    },
    // Undo: back to the picks before the last update, saved straight away.
    undoCartEdit(item) {
      const undo = this.cartEditUndoable(item) && this.cartEditNote.undo;
      if (!undo) return;
      saysoTriggerHaptic("selection");
      this.setCartEditNote(null);
      Object.assign(item, undo);
      item.undoing = true;
      this.runAutoSave(item.id);
    },
    // Delete link in the in-cart status line: takes that hoodie out of the cart. If it was the only one, a fresh hoodie takes its place.
    // The × on a header row: mechanical close sound, then remove (cart hoodies via deleteCartItem, so the last one leaves a fresh draft).
    removeFromX(item) {
      if (!item || (item.cartBacked && this.cartBusy)) return;
      saysoPlaySound("close", { theme: "mech" });
      if (item.cartBacked) this.deleteCartItem(item);
      else this.removeItem(item.id);
    },
    deleteCartItem(item) {
      if (!item?.cartBacked || this.cartBusy) return;
      this.holdPageForEmpty();
      const openDraft = this.items.find((candidate) => !candidate.cartBacked && candidate.id === this.openId);
      if (this.items.length <= 1) {
        this.items = [...this.items, this.ensureItemSizeAvailable(this.createItem(defaultSelectionSource()))];
      }
      this.removeItem(item.id);
      // The last one: the builder left opens on its own collapse (watchCartEmptied), not in place.
      if (this.items.some((candidate) => candidate.cartBacked)) this.releaseEmptyHold();
      else if (this.openId !== openDraft?.id) this.openId = null;
    },
    // Live in-cart subtotal: cart truth only (an unsaved edit counts as its cart version).
    cartSubtotalItems() {
      return this.items
        .filter((item) => item.cartBacked)
        .map((item) =>
          this.cartBackedItemChanged(item) && item.cartFields ? { ...item, ...item.cartFields } : item,
        );
    },
    cartSubtotalView() {
      const items = this.cartSubtotalItems();
      const subtotal = this.subtotalCents(items);
      const discounted = this.discountedSubtotalCents(items);
      return {
        quantity: items.length,
        hasDiscount: subtotal > discounted,
        label: `${subtotal ? Math.round((1 - discounted / subtotal) * 100) : 0}% OFF`,
        original: this.wholePrice(subtotal),
        price: this.wholePrice(discounted),
      };
    },
    // What the footer shows: the held snapshot while tucked/rising, else the live cart total.
    // Emptied cart: the footer fades out (180ms) on the last numbers it had, not "0 hoodies $0".
    subtotalView() {
      if (this.subtotalHeld) return this.subtotalHeld;
      const view = this.cartSubtotalView();
      if (view.quantity) saysoLastSubtotal.set(this.$root, view);
      return view.quantity ? view : saysoLastSubtotal.get(this.$root) || view;
    },
    // A builder is open (a draft, an in-cart row, the just-added one before its fold): the footer
    // is never up then. A row folding shut counts as closed, so the footer can ride its edge.
    builderOpen() {
      const id = this.openId;
      return id !== null && this.foldedCartId !== id && this.items.some((item) => item.id === id);
    },
    // Add another hoodie tap: slide the footer below the screen so the new builder has focus.
    tuckSubtotal() {
      if (this.subtotalTucked) return;
      window.clearTimeout(this.subtotalReleaseTimer);
      // Tucked mid-slide: the slot sinks out of sight in ~150ms, so a price cue still waiting
      // is dropped (unless the rows above are still rolling on the same cue).
      if (this.subtotalPriceSliding) {
        const rowsLeft = this.summaryPriceAnimating ? this.summaryRollEndsAt - performance.now() : 0;
        window.saysoSound?.priceCut?.(Math.max(150, rowsLeft));
      }
      this.detachFooterFromClose();
      // New numbers whose slide hasn't started yet (released this same tick): it sinks on the
      // ones it was showing, and slides to the new ones when it comes back up.
      const pending = saysoFooterPending.get(this.$root);
      if (!this.subtotalHeld && pending) this.subtotalHeld = pending;
      if (!this.subtotalHeld) {
        const view = this.cartSubtotalView();
        this.subtotalHeld = view.quantity ? view : null;
      }
      this.subtotalTucked = true;
    },
    // Slide it back up. withFold: ride the closing builder's bottom edge (see startFoldClose).
    // Otherwise CSS slides it (520ms, x-collapse curve). Held numbers switch to the cart's when
    // it is ~80% up, so the slot plays where it can be seen. Reduced motion: instant.
    untuckSubtotal({ withFold = false } = {}) {
      if (!this.subtotalTucked || this.builderOpen()) return; // never up over an open builder
      this.subtotalTucked = false;
      window.clearTimeout(this.subtotalReleaseTimer);
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.subtotalHeld = null;
        return;
      }
      const release = () => { this.subtotalHeld = null; };
      const close = withFold ? this.foldClose : null;
      const foot = this.$root?.querySelector(".alpine-cart-subtotal");
      if (!close || !foot) {
        this.subtotalReleaseTimer = window.setTimeout(release, 300);
        return;
      }
      const drop = foot.offsetHeight + 12; // matches the tucked translateY(calc(100% + 12px))
      foot.style.transition = "none";
      foot.style.transform = `translateY(${drop}px)`;
      close.footer = { foot, drop, release, released: false };
    },
    // finished: it is up, so settle without a CSS tail. Otherwise (tucked again mid-close) CSS
    // carries it on from where it is.
    detachFooterFromClose({ finished = false } = {}) {
      const footer = this.foldClose?.footer;
      if (!footer) return null;
      this.foldClose.footer = null;
      const { style } = footer.foot;
      style.transform = "";
      if (finished) void footer.foot.offsetHeight; // apply the last step with transition: none
      style.transition = "";
      return footer;
    },
    // The one close after add to cart: builder and "in your cart" row collapse together
    // (x-collapse 520ms) under the summary row. Near the end of the page the browser would pull
    // the scroll back as the page gets shorter, pinning the card's bottom edge mid-close; so the
    // page keeps its height for the close and is scrolled to where it comes to rest on the
    // collapse's own curve, and the edge moves in one go. A footer that is down rides that edge
    // (as far up as the edge is through its trip, so they start and stop on the same frames).
    // Call in the same task that closes it, before Alpine starts the collapse.
    startFoldClose(card, id, { cueMoves = false } = {}) {
      this.endFoldClose();
      const parts = Array.from(
        card?.querySelectorAll(":scope > .alpine-hoodie-body, :scope > .alpine-hoodie-footer") || [],
      ).filter((el) => el.offsetHeight > 0);
      if (!card?.isConnected || !parts.length) return;
      const viewport = window.innerHeight;
      const visible = (y) => Math.min(y, viewport);
      const startScroll = window.scrollY;
      const startHeight = parts.reduce((sum, el) => sum + el.offsetHeight, 0);
      const pageHeight = document.scrollingElement.scrollHeight;
      // The closed layout, read once and never painted: parts squashed by max-height (leaves
      // the height transitions alone); their borders go at the end too.
      const saved = parts.map((el) => el.style.maxHeight);
      parts.forEach((el) => { el.style.maxHeight = "0px"; });
      const restoreCue = cueMoves ? this.cueAfterClose(card) : () => {};
      const borders = parts.reduce((sum, el) => sum + el.offsetHeight, 0);
      const maxScroll = Math.max(0, document.scrollingElement.scrollHeight - borders - viewport);
      const rect = card.getBoundingClientRect();
      const top = rect.top + window.scrollY;
      const bottom = rect.bottom - borders + window.scrollY;
      restoreCue();
      parts.forEach((el, i) => { el.style.maxHeight = saved[i]; });
      window.scrollTo(0, startScroll);
      // Rest: the same scroll if it still fits, nudged so the closed row is on screen.
      let endScroll = Math.min(startScroll, maxScroll);
      if (top < endScroll) endScroll = top;
      else if (bottom > endScroll + viewport) endScroll = bottom - viewport;
      endScroll = Math.max(0, Math.min(endScroll, maxScroll));
      const from = visible(card.getBoundingClientRect().bottom);
      const to = visible(bottom - endScroll);
      const scrolls = Math.abs(endScroll - startScroll) >= 1;
      if (scrolls) document.documentElement.style.minHeight = `${pageHeight}px`;
      const close = { seq: ++this.foldCloseSeq, id, frame: 0, timer: 0, footer: null, progress: 0, endScroll, scrolls };
      const current = () => this.foldClose?.seq === close.seq; // this.foldClose reads back as Alpine's proxy
      const step = () => {
        if (!current()) return;
        if (this.foldedCartId !== id) return this.endFoldClose(); // moved on (opened a row, Add another)
        const shut = parts.every((el) => window.getComputedStyle(el).display === "none");
        const left = shut ? 0 : parts.reduce((sum, el) => sum + el.offsetHeight, 0);
        const collapsed = Math.min(1, Math.max(0, 1 - left / startHeight));
        if (scrolls) window.scrollTo(0, startScroll + (endScroll - startScroll) * collapsed);
        const travel = from - to;
        const edge = travel >= 1 ? (from - visible(card.getBoundingClientRect().bottom)) / travel : collapsed;
        close.progress = Math.max(close.progress, Math.min(1, edge));
        const footer = close.footer;
        if (footer) {
          footer.foot.style.transform = `translateY(${(footer.drop * (1 - close.progress)).toFixed(2)}px)`;
          if (!footer.released && close.progress >= 0.8) {
            footer.released = true;
            footer.release();
          }
        }
        if (shut) return this.endFoldClose({ finished: true });
        close.frame = window.requestAnimationFrame(step);
      };
      close.frame = window.requestAnimationFrame(step);
      close.timer = window.setTimeout(() => current() && this.endFoldClose(), 1500); // safety net
      this.foldClose = close;
    },
    // For startFoldClose's closed-layout read: IN CART ↑ moves from above this row to under it
    // (the add's close plays both). Hides the old one and puts a plain copy under the row;
    // returns the undo. Synchronous, so never painted.
    cueAfterClose(card) {
      const entry = card.closest(".alpine-hoodie-entry");
      const previous = entry?.previousElementSibling;
      const old = previous?.matches(".alpine-hoodie-entry")
        ? previous.querySelector(":scope > .alpine-cart-cue")
        : null;
      const oldDisplay = old?.style.display;
      if (old) old.style.display = "none";
      const probe = document.createElement("div");
      probe.className = "alpine-cart-cue";
      probe.innerHTML = '<div class="alpine-cart-cue-separator"><span class="alpine-cart-cue-separator-label">in cart ↑</span></div>';
      entry?.append(probe);
      return () => {
        probe.remove();
        if (old) old.style.display = oldDisplay;
      };
    },
    endFoldClose({ finished = false } = {}) {
      const close = this.foldClose;
      if (!close) return;
      const footer = this.detachFooterFromClose({ finished });
      if (footer && !footer.released) footer.release();
      this.foldClose = null;
      window.cancelAnimationFrame(close.frame);
      window.clearTimeout(close.timer);
      if (close.scrolls) {
        document.documentElement.style.minHeight = "";
        if (this.foldedCartId === close.id) window.scrollTo(0, close.endScroll);
      }
      if (this.foldedCartId === close.id) this.foldedCartId = null;
    },
    // Footer numbers changed while it is up (add, delete, an edit saved): play the rows' slot
    // (same class, 380ms). Not on first show (from 0), while tucked, or for reduced motion.
    // Also brings the footer back if the hoodie being built goes away (× on it, cart sync).
    watchCartSubtotalChanges() {
      let previous = null;
      let slideTimer = null;
      let slideSeq = 0;
      Alpine.effect(() => {
        const view = this.subtotalView();
        const key = `${view.quantity}|${view.label}|${view.original}|${view.price}`;
        const before = previous;
        previous = { quantity: view.quantity, label: view.label, price: view.price, key, view };
        if (!before || before.key === key || !before.quantity || !view.quantity) return;
        saysoFooterPending.set(this.$root, before.view); // until its slide starts (tuckSubtotal)
        queueMicrotask(() => {
          saysoFooterPending.delete(this.$root);
          if (this.subtotalTucked) return;
          if (saysoReducedMotion()) return;
          window.clearTimeout(slideTimer);
          const restart = this.subtotalPriceSliding;
          this.subtotalPriceSliding = false;
          const slide = ++slideSeq;
          // Same frame as the new numbers (the release runs inside the fold's frame callback, so
          // a rAF here showed them unrolled for a frame); a slot still playing restarts.
          this.$nextTick(() => {
            if (slide !== slideSeq || this.subtotalTucked) return; // went back down meanwhile
            const foot = this.$root?.querySelector(".alpine-cart-subtotal");
            if (restart) saysoFlushStyles(foot);
            this.subtotalPriceSliding = true;
            if (!document.hidden) saysoPlayPriceSound(380, { percent: before.label, price: before.price }, { percent: view.label, price: view.price });
            slideTimer = window.setTimeout(() => { if (slide === slideSeq) this.subtotalPriceSliding = false; }, 380);
          });
        });
      });
      // The one rule for the footer: down whenever a builder is open, however it opened (page
      // load with a cart, the just-added builder, an in-cart row, the bag emptying...).
      Alpine.effect(() => {
        if (this.builderOpen() && !this.subtotalTucked) queueMicrotask(() => this.builderOpen() && this.tuckSubtotal());
      });
      const nothingBeingBuilt = () =>
        this.subtotalTucked &&
        !this.builderOpen() &&
        !this.reopenTuck && // an opened in-cart row brings it back itself
        !this.cartBusy &&
        !this.addingItem &&
        !this.items.some((item) => !item.cartBacked);
      Alpine.effect(() => {
        if (nothingBeingBuilt()) queueMicrotask(() => nothingBeingBuilt() && this.untuckSubtotal());
      });
      Alpine.effect(() => {
        // Any way the opened in-cart row stops being open: tapped shut, another row, delete...
        if (this.reopenTuck && this.openId !== this.reopenTuck.id) queueMicrotask(() => this.releaseSubtotalForRow());
      });
    },
    // The bag goes from hoodies to none (× on the last row, the bag drawer, another tab): the
    // builder opens as on a fresh visit. The footer goes with the last row (showCartSubtotal).
    watchCartEmptied() {
      let had = false;
      Alpine.effect(() => {
        const has = this.items.some((item) => item.cartBacked);
        const was = had;
        had = has;
        if (was && !has) queueMicrotask(() => this.openBuilderAfterEmpty());
      });
    },
    // Opens on the x-collapse (520ms) once the row is on the page (followOpenToAnchor moves
    // the page with it only when it has to). Under the bag
    // drawer it waits for the drawer to close, so the open is seen. Reduced motion: instant.
    openBuilderAfterEmpty() {
      const seq = ++this.emptyOpenSeq;
      this.emptyOpenPending = true;
      const open = () => {
        if (seq !== this.emptyOpenSeq) return; // superseded
        this.emptyOpenPending = false;
        const draft = this.items.find((item) => !item.cartBacked);
        const card = draft && this.itemCardElement(draft.id);
        // Opened by hand meanwhile, or put back (a failed remove).
        if (this.openId !== null || !card || this.items.some((item) => item.cartBacked)) return this.releaseEmptyHold();
        const reduceMotion = saysoReducedMotion();
        this.endFoldClose();
        this.foldedCartId = null;
        if (reduceMotion) {
          card.classList.add("is-instant-open");
          window.setTimeout(() => card.classList.remove("is-instant-open"), 120);
        }
        this.openId = draft.id;
        // Out of view (under the header, or low on the screen): it glides up to the anchor, as a
        // tapped row does. Else it stays put. Either way the glide takes over the page-height hold.
        const top = card.getBoundingClientRect().top;
        const stay = top >= this.builderAnchorOffset() - 1 && top <= window.innerHeight * 0.6;
        window.clearTimeout(this.emptyHoldTimer);
        this.followOpenToAnchor(draft.id, { stay });
      };
      const whenShown = () => this.$nextTick(() => window.requestAnimationFrame(open));
      if (document.querySelector("[popover][data-state='open']")) {
        document.addEventListener("site-panel:close", () => window.setTimeout(whenShown, 160), { capture: true, once: true });
        return;
      }
      whenShown();
    },
    // The last row (and the footer's room under it) goes before the builder opens: the page
    // keeps its height meanwhile so the browser doesn't pull the scroll up (a jump).
    holdPageForEmpty() {
      if (this.builderAnchorHold || !this.items.some((item) => item.cartBacked)) return;
      this.builderAnchorHold = document.scrollingElement.scrollHeight;
      document.documentElement.style.minHeight = `${this.builderAnchorHold}px`;
      window.clearTimeout(this.emptyHoldTimer);
      this.emptyHoldTimer = window.setTimeout(() => this.releaseEmptyHold(), 4000); // not emptied after all
    },
    releaseEmptyHold() {
      window.clearTimeout(this.emptyHoldTimer);
      if (this.builderAnchorFrame) return; // a glide owns it now
      this.releaseBuilderAnchorHold();
    },
    showCartSubtotal() {
      // Also shows while another hoodie is being built (it is a sticky footer row then).
      return this.cartSubtotalItems().length >= 1;
    },
	    // Close the just-added builder (the flying "1" is already measured from the button) in
	    // one go: it and its "in your cart" row collapse under the summary row, which turns into a
	    // plain row like the ones above, flashes once and takes focus; the footer rises with it
	    // (startFoldClose). Moving on (Edit, open a row, Add another) cancels the close.
	    foldAfterAdd() {
	      const id = this.openId;
	      if (id === null) return;
	      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	      const card = this.itemCardElement(id);
	      this.clearBuilderAnchorMotion(); // a still-running glide to this builder would fight the close
	      this.foldedCartId = id;
	      // Reads the open layout, so before openId changes. IN CART ↑ moves from above this row to
	      // under it during the same close.
	      this.startFoldClose(card, id, { cueMoves: this.cueHeldIds.includes(id) });
	      this.cueHeldIds = [];
	      this.untuckSubtotal({ withFold: true });
	      this.openId = null;
	      if (!this.foldClose) {
	        window.setTimeout(() => {
	          if (this.foldedCartId === id) this.foldedCartId = null;
	        }, 520); // the builder's x-collapse.duration.520ms
	      }
	      card?.querySelector(".alpine-hoodie-toggle")?.focus({ preventScroll: true });
	      if (reduceMotion) return;
	      window.setTimeout(() => {
	        if (this.foldedCartId !== id) return;
	        this.foldFlashId = id;
	        window.setTimeout(() => {
	          if (this.foldFlashId === id) this.foldFlashId = null;
	        }, 700);
	      }, 300); // flash overlaps the 520ms close
	    },
	    markRecentlyCarted(items = []) {
	      this.recentlyCartedItemIds = items.map((item) => item.id).filter(Boolean);
	      if (!this.recentlyCartedItemIds.length) return;

	      window.setTimeout(() => {
	        this.recentlyCartedItemIds = [];
	      }, 1400);
	    },
	    showCartConfirmationState(submittedItems, cart = null) {
	      const cartBackedItems = submittedItems.map((item) => {
	        const nextItem = {
	          ...item,
	          cartBacked: true,
	        };
	        nextItem.cartVariantId = String(this.variantFor(nextItem)?.id || "");
	        nextItem.cartFields = this.cartFieldsOf(nextItem);
	        return nextItem;
	      });
	      const nextBuilderVariantIds = new Set(
	        cartBackedItems
	          .map((item) => String(this.variantFor(item)?.id || "").trim())
	          .filter(Boolean),
	      );

	      this.items = cartBackedItems;
	      this.openId = cartBackedItems[cartBackedItems.length - 1]?.id || null;
	      this.cartHadBuilderItems = cartBackedItems.length > 0;
	      this.editingExistingCart = cartBackedItems.length > 0;
	      this.queueDirty = false;
	      this.existingCartBuilderVariantIds = Array.from(nextBuilderVariantIds);
	      this.cartItemCount = Math.max(
	        0,
	        Number(cart?.item_count || this.cartItemCount || 0),
	      );
	      this.markRecentlyCarted(cartBackedItems);
	      this.updateHeaderCartCount(this.cartItemCount);
	      this.cartStatus = "Added to cart";
	    },
	    async confirmCartThenNavigate(
	      submittedItems,
	      cart = null,
	      { stateAlreadyShown = false } = {},
	    ) {
	      if (stateAlreadyShown) {
	        this.cartItemCount = Math.max(
	          0,
	          Number(cart?.item_count || this.cartItemCount || 0),
	        );
	        this.updateHeaderCartCount(this.cartItemCount);
	      } else {
	        this.showCartConfirmationState(submittedItems, cart);
	      }
	      // replaceExistingCartItems sets "Updating"; land back on the confirmation.
	      this.cartStatus = "Added to cart";
	      await this.$nextTick();
	      await new Promise((resolve) => setTimeout(resolve, 300)); // frees cartBusy (AAH) mid-fold

	      // Stay in the builder after ATC confirmation (no /cart redirect).
	      if (this.cartStatus === "Added to cart") {
	        window.setTimeout(() => {
	          if (this.cartStatus === "Added to cart") this.cartStatus = "";
	        }, 900);
	      }
	    },
	    async continueBuildingAfterCartCommit(submittedItems, cart = null, hold = 0) {
      const cartBackedItems = submittedItems.map((item) => {
        const nextItem = {
          ...item,
          cartBacked: true,
        };
        nextItem.cartVariantId = String(this.variantFor(nextItem)?.id || "");
        nextItem.cartFields = this.cartFieldsOf(nextItem);
        return nextItem;
      });
      const source =
        cartBackedItems[cartBackedItems.length - 1] ||
        submittedItems[submittedItems.length - 1] ||
        this.items[0];
      const next = this.ensureItemSizeAvailable(this.createItem(source));
      next.colour = preferredValue(this.colours, ["white", "natural", "cream"]);
      next.cartBacked = false;

      const nextBuilderVariantIds = new Set(
        cartBackedItems
          .map((item) => String(this.variantFor(item)?.id || "").trim())
          .filter(Boolean),
      );

      this.cartHadBuilderItems = cartBackedItems.length > 0;
      this.editingExistingCart = cartBackedItems.length > 0;
      this.queueDirty = true;
      this.existingCartBuilderVariantIds = Array.from(nextBuilderVariantIds);
	      this.cartItemCount = Math.max(
	        0,
	        Number(cart?.item_count || this.cartItemCount || 0),
	      );
	      this.markRecentlyCarted(cartBackedItems);
	      this.updateHeaderCartCount(this.cartItemCount);
      this.setCartModeUrl("edit");

      this.openId = null;
      await this.$nextTick();
      await new Promise((resolve) => setTimeout(resolve, 260));

      this.items = [...cartBackedItems, next];
      await this.$nextTick();

      window.requestAnimationFrame(() => {
        this.openId = next.id;
        void this.revealNewBuilder(next.id, hold);
      });
    },
    cartItemsFor(items = this.items) {
      return items.map((item) => {
        const variant = this.variantFor(item);

        if (!variant?.available) {
          throw new Error("Unavailable hoodie selection");
        }

        return {
          id: String(variant.id),
          quantity: 1,
          properties: {},
        };
      });
    },
    async updateCartBackedItem(item, { quiet = false } = {}) {
      if (
        this.cartBusy ||
        this.cartContextLoading ||
        !this.cartBackedItemChanged(item) ||
        !window.fetch
      ) {
        return;
      }

      const previousState = {
        cartHadBuilderItems: this.cartHadBuilderItems,
        cartItemCount: this.cartItemCount,
        editingExistingCart: this.editingExistingCart,
        existingCartBuilderVariantIds: this.existingCartBuilderVariantIds.slice(),
        items: this.items.map((candidate) => ({ ...candidate })),
        openId: this.openId,
        queueDirty: this.queueDirty,
        summaryDiscountQuantity: this.summaryDiscountQuantity,
      };

      const fields = this.changedCartFields(item);
      const undo = item.undoing ? null : { ...item.cartFields };
      const undoing = Boolean(item.undoing);
      delete item.undoing;
      this.cartBusy = true;
      this.savingItemId = item.id;
      this.savingFields = fields;
      this.cartStatus = quiet ? `Updating ${this.cartFieldsCopy(fields)}` : "Updating cart";

      try {
        const cartBackedItems = this.items.filter((candidate) => candidate.cartBacked);
        // What this save sends: a pick made while it's in flight stays changed and saves next.
        const sent = cartBackedItems.map((candidate) => ({
          candidate,
          variantId: this.currentVariantId(candidate),
          fields: this.cartFieldsOf(candidate),
        }));
        const previousCartBackedItems = previousState.items.filter(
          (candidate) => candidate.cartBacked,
        );
        const cartPayload = this.cartItemsFor(cartBackedItems);
        const cart = await this.replaceExistingCartItems(
          cartPayload,
          previousCartBackedItems,
        );
        const nextBuilderVariantIds = new Set(
          cartPayload.map((line) => String(line.id)).filter(Boolean),
        );
        const hasCueItems = this.items.some((candidate) => !candidate.cartBacked);

        sent.forEach(({ candidate, variantId, fields: sentFields }) => {
          candidate.cartVariantId = variantId;
          candidate.cartFields = sentFields;
          candidate.cartBacked = true;
        });
        this.existingCartBuilderVariantIds = Array.from(nextBuilderVariantIds);
        this.cartHadBuilderItems = cartBackedItems.length > 0;
        this.editingExistingCart = cartBackedItems.length > 0;
        this.queueDirty = hasCueItems;
        this.cartItemCount = Math.max(0, Number(cart?.item_count || 0));
        this.updateHeaderCartCount(this.cartItemCount);
        if (window.saysoPanels?.isOpen?.("cart-drawer")) window.saysoCartDrawer?.refresh?.();
        if (quiet) {
          const copy = `Updated ${this.cartFieldsCopy(fields)}.`;
          if (!this.cartBackedItemChanged(item)) {
            this.setCartEditNote({ id: item.id, copy, undo: undoing || !fields.length ? null : undo });
          }
          this.cartStatus = copy;
          window.setTimeout(() => {
            if (this.cartStatus === copy) this.cartStatus = "";
          }, 1800);
        } else {
          this.markRecentlyCarted([item]);
          this.cartStatus = "Hoodie updated";
          window.setTimeout(() => {
            if (this.cartStatus === "Hoodie updated") this.cartStatus = "";
          }, 1800);
        }
      } catch (error) {
        // Back to what's in the cart: every in-cart hoodie shows its cart picks again.
        const cartFieldsById = new Map(
          previousState.items.filter((candidate) => candidate.cartFields).map((candidate) => [candidate.id, candidate.cartFields]),
        );
        this.items.forEach((candidate) => {
          if (!candidate.cartBacked) return;
          window.clearTimeout(saysoAutoSaveTimers.get(candidate.id));
          saysoAutoSaveTimers.delete(candidate.id);
          const saved = candidate.cartFields || cartFieldsById.get(candidate.id);
          if (saved && this.cartBackedItemChanged(candidate)) Object.assign(candidate, saved);
        });
        this.openId = previousState.openId;
        this.cartHadBuilderItems = previousState.cartHadBuilderItems;
        this.cartItemCount = previousState.cartItemCount;
        this.editingExistingCart = previousState.editingExistingCart;
        this.existingCartBuilderVariantIds =
          previousState.existingCartBuilderVariantIds;
        this.queueDirty = previousState.queueDirty;
        this.summaryDiscountQuantity = previousState.summaryDiscountQuantity;
        this.updateHeaderCartCount(this.cartItemCount);
        this.setSummaryDiscountQuantity(this.discountQuantity(), false);
        const failed = `Couldn't update ${this.cartFieldsCopy(fields)}. Please try again.`;
        this.setCartEditNote({ id: item.id, copy: failed, error: true }, 6000);
        this.cartStatus = failed;
        await this.refreshCartCount();
      } finally {
        this.cartBusy = false;
        this.savingItemId = null;
        this.savingFields = [];
      }
    },
    async syncCartBackedItemsAfterInlineRemove(previousState) {
      if (!window.fetch) return;

      this.cartBusy = true;
      this.cartStatus = "Updating cart";

      try {
        const cartBackedItems = this.items.filter((item) => item.cartBacked);
        const cartPayload = this.cartItemsFor(cartBackedItems);
        const previousCartBackedItems = previousState.items.filter(
          (item) => item.cartBacked,
        );
        const cart = await this.replaceExistingCartItems(
          cartPayload,
          previousCartBackedItems,
        );
        const nextBuilderVariantIds = new Set(
          cartPayload.map((line) => String(line.id)).filter(Boolean),
        );
        const hasCueItems = this.items.some((item) => !item.cartBacked);

        this.existingCartBuilderVariantIds = Array.from(nextBuilderVariantIds);
        this.cartHadBuilderItems = cartBackedItems.length > 0;
        this.editingExistingCart = cartBackedItems.length > 0;
        this.queueDirty = this.editingExistingCart && hasCueItems;
        cartBackedItems.forEach((item) => this.markItemCartSynced(item));

        if (this.editingExistingCart) {
          this.setCartModeUrl("edit");
        } else {
          this.setCartModeUrl("");
        }

        this.cartItemCount = Math.max(0, Number(cart?.item_count || 0));
        this.updateHeaderCartCount(this.cartItemCount);
        this.cartStatus = "Cart updated";
        window.setTimeout(() => {
          if (this.cartStatus === "Cart updated") this.cartStatus = "";
        }, 1800);
      } catch (error) {
        this.items = previousState.items;
        this.openId = previousState.openId;
        this.cartHadBuilderItems = previousState.cartHadBuilderItems;
        this.cartItemCount = previousState.cartItemCount;
        this.editingExistingCart = previousState.editingExistingCart;
        this.queueDirty = previousState.queueDirty;
        this.summaryDiscountQuantity = previousState.summaryDiscountQuantity;
        this.updateHeaderCartCount(this.cartItemCount);
        this.setSummaryDiscountQuantity(this.discountQuantity(), false);
        this.cartStatus = "Could not update cart. Please try again.";
        await this.refreshCartCount();
      } finally {
        this.cartBusy = false;
      }
    },
		    async addToCart({ continueBuilding = false, stayOnPage = false, confirmThenCart = false, flyFrom = null } = {}) {
		      if (this.cartBusy) return;

	      this.cartBusy = true;
	      this.cartStatus =
	        this.editingExistingCart && this.queueDirty ? "Updating" : "Adding";

	      let previousConfirmState = null;
	      let keepSubtotalTucked = continueBuilding; // Add another: stays down for the new builder
	      // Add another with a hoodie being built: the rows keep their discount until the next
	      // builder's bubble (as addItem). Taken now: the cart count rises before the rows swap.
	      let hold = continueBuilding && !saysoReducedMotion() ? this.holdSummaryDiscount() : 0;

	      try {
		        if (this.editingExistingCart && !this.queueDirty) {
	          if (continueBuilding || stayOnPage) {
	            await this.addItem();
	            return;
	          }

		          this.cartStatus = "";
		          saysoOpenCart();
		          return;
		        }

        const submittedItems = this.items.slice();
	        const items = this.cartItemsFor(submittedItems);
	        // Flight after a successful add: from the pressed button, showing how many were added.
	        // Measure it now: showCartConfirmationState hides the button before the add returns.
	        const flyButton = flyFrom || this.$root?.querySelector(".alpine-cart-button");
	        const flySource = window.saysoFlyAnchor?.(flyButton) || flyButton;
	        const addedCount = submittedItems.filter((item) => !item.cartBacked).length || 1;
        if (confirmThenCart) {
          previousConfirmState = {
            cartHadBuilderItems: this.cartHadBuilderItems,
            cartItemCount: this.cartItemCount,
            editingExistingCart: this.editingExistingCart,
            existingCartBuilderVariantIds: this.existingCartBuilderVariantIds.slice(),
            items: this.items.slice(),
            openId: this.openId,
            queueDirty: this.queueDirty,
            summaryDiscountQuantity: this.summaryDiscountQuantity,
          };
          this.cueHeldIds = submittedItems.filter((item) => !item.cartBacked).map((item) => item.id);
          this.showCartConfirmationState(submittedItems);
          await this.$nextTick();
        }

	        if (!window.fetch) {
	          this.cartStatus = "Opening cart";
	          saysoSubmitCartAddFallback(items);
	          return;
	        }

	        // Haptic and the flying "1" start on tap, alongside the cart request (not after it).
	        // The bag count still only ticks on a successful add; on failure the catch restores state.
	        saysoTriggerHaptic("success");
	        const flight = saysoPlayAddFlight(flySource, addedCount);

		        if (this.editingExistingCart) {
		          const cart = await this.replaceExistingCartItems(items);
	          if (!continueBuilding && !stayOnPage && !confirmThenCart) {
	            await flight;
	            this.cartStatus = "";
	            this.queueDirty = false;
	            this.updateHeaderCartCount(Math.max(0, Number(cart?.item_count || 0)));
	            // Drawer opens in place: once this add settles, redraw IN CART rows from Shopify.
	            if (saysoOpenCart()) window.setTimeout(() => void this.syncWithCart(), 0);
	            return;
          }

          if (confirmThenCart) {
            await flight; // fold once the "1" has landed, not under it
            this.updateHeaderCartCount(Math.max(0, Number(cart?.item_count || this.cartItemCount || 0))); // count ticks on landing
            await saysoAfterLandingBeat();
            this.foldAfterAdd();
            await this.confirmCartThenNavigate(submittedItems, cart, {
              stateAlreadyShown: true,
            });
            return;
          }

          await this.continueBuildingAfterCartCommit(submittedItems, cart, hold);
          hold = 0;
		          this.cartStatus = "Added to cart";
          window.setTimeout(() => {
            if (this.cartStatus === "Added to cart") this.cartStatus = "";
          }, 1800);
		          return;
		        }

	        const response = await saysoCart.add(items);

        if (!response.ok) {
          const responseText = await response.text().catch(() => "");
          const plainResponse = responseText
            .replace(/<[^>]*>/g, " ")
            .replace(/\s+/g, " ")
            .trim();

          if (saysoIsShopifyVerificationResponse(response, responseText)) {
            if (saysoIsLocalThemePreview()) {
              this.cartStatus = saysoLocalCartPreviewMessage;
              return;
            }

	            this.cartStatus = "Opening cart";
	            this.queueDirty = false;
	            window.location.assign(this.cartPermalink(items, "cart"));
	            return;
	          }

          let errorBody = null;
          try {
            errorBody = responseText ? JSON.parse(responseText) : null;
          } catch (parseError) {
            errorBody = null;
          }
          const fallbackMessage = plainResponse.slice(0, 180);
          throw new Error(
            errorBody?.description ||
              errorBody?.message ||
              fallbackMessage ||
              `Shopify rejected the cart request (${response.status})`,
          );
        }

        if (!continueBuilding && !stayOnPage && !confirmThenCart) {
          await flight;
          this.cartStatus = "";
          if (saysoOpenCart()) window.setTimeout(() => void this.syncWithCart(), 0);
          return;
        }

        const cart = await this.refreshCartCount();
        if (confirmThenCart) {
          await flight; // fold once the "1" has landed, not under it
          await saysoAfterLandingBeat();
          this.foldAfterAdd();
          await this.confirmCartThenNavigate(submittedItems, cart, {
            stateAlreadyShown: true,
          });
          return;
        }

        await this.continueBuildingAfterCartCommit(submittedItems, cart, hold);
        hold = 0;
	        this.cartStatus = "Added to cart";
        window.setTimeout(() => {
          if (this.cartStatus === "Added to cart") this.cartStatus = "";
        }, 1800);
	      } catch (error) {
        if (previousConfirmState) {
          this.items = previousConfirmState.items;
          this.openId = previousConfirmState.openId;
          this.cartHadBuilderItems = previousConfirmState.cartHadBuilderItems;
          this.cartItemCount = previousConfirmState.cartItemCount;
          this.editingExistingCart = previousConfirmState.editingExistingCart;
          this.existingCartBuilderVariantIds =
            previousConfirmState.existingCartBuilderVariantIds;
          this.queueDirty = previousConfirmState.queueDirty;
          this.summaryDiscountQuantity = previousConfirmState.summaryDiscountQuantity;
          this.updateHeaderCartCount(this.cartItemCount);
        }
        this.cartStatus = `Could not add this item. ${error.message}`;
        keepSubtotalTucked = false;
      } finally {
        if (hold) this.releaseSummaryDiscount(hold); // no next builder (error, cart page): let go
        this.cartBusy = false;
        if (this.cueHeldIds.length) this.cueHeldIds = [];
        if (!keepSubtotalTucked) this.untuckSubtotal();
	      }
		    },
			    async replaceExistingCartItems(items, existingItems = []) {
			      this.cartStatus = "Updating";
		      const desiredQuantities = items.reduce((lines, item) => {
		          const id = String(item.id || "").trim();
		          if (!id) return lines;
		        lines[id] = (lines[id] || 0) + Number(item.quantity || 1);
		          return lines;
		      }, {});
	      const existingIds = new Set(this.existingCartBuilderVariantIds);
	      existingItems.forEach((item) => {
	        const id = String(this.variantFor(item)?.id || "").trim();
	        if (id) existingIds.add(id);
	      });
	      const newItems = Object.entries(desiredQuantities)
	        .filter(([id]) => !existingIds.has(id))
	        .map(([id, quantity]) => ({ id, quantity, properties: {} }));

	      if (newItems.length) {
	        const addNewResponse = await saysoCart.add(newItems);

	        if (!addNewResponse.ok) {
	          throw new Error("Could not add updated cart items");
	        }
	      }

	      const updates = {};
	      existingIds.forEach((id) => {
	        updates[id] = 0;
	      });
	      Object.assign(updates, desiredQuantities);

	      const updateResponse = await saysoCart.update(updates);

		      if (!updateResponse.ok) {
		        throw new Error("Could not update existing cart items");
		      }

		      return updateResponse.json();
		    },
  };
};
